import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Chip,
  IconButton,
  CircularProgress,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Menu,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  FormControl,
  InputLabel,
  Autocomplete,
  Tooltip
} from '@mui/material';
import {
  Search as SearchIcon,
  Map as MapIcon,
  Add as AddIcon,
  Warning as WarningIcon,
  FilterList as FilterIcon,
  Close as CloseIcon,
  Settings as SettingsIcon,
  PersonAdd as PersonAddIcon,
  PersonRemove as PersonRemoveIcon
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth.tsx';
import { requestUnitUnclaim, sendUnitUnclaimBeacon } from '../utils/unitUnclaim';
import { usePermissions } from '../hooks/usePermissions.tsx';
import { useSocket } from '../contexts/SocketContext.tsx';
import apiClient from '../utils/apiClient';
import { http } from '../lib/http';
import { useToast } from '../contexts/ToastContext.tsx';
import { useConfig } from '../contexts/ConfigContext.tsx';
import { audioNotifications } from '../utils/audioNotifications';
// import CallList from './CallList.tsx';
// import UnitList from './UnitList.tsx';
import CallDetails from './CallDetails.tsx';
import CreateCallModal from './CreateCallModal.tsx';
import ClosedCallsView from './ClosedCallsView.tsx';
import AccountDialog from './AccountDialog.tsx';
import ShiftCommanderInterface from './ShiftCommanderInterface.tsx';
import FilterDialog from './FilterDialog.tsx';
import DispatcherHeader from './DispatcherHeader.tsx';
import AdminInterface from './AdminInterface.tsx';
import MapModal from './MapModal.tsx';
import { Unit, Call, UnitAssignment, User, UnitType } from '../types/index.ts';

interface DispatcherInterfaceProps {
  onReturnToShiftSelection?: () => void;
}

const DispatcherInterface: React.FC<DispatcherInterfaceProps> = ({ onReturnToShiftSelection }) => {
  const { logout, user } = useAuth();
  const { hasPermission } = usePermissions();
  const socket = useSocket();
  const { showSuccess, showError, showWarning, showInfo } = useToast();
  const { callCodes } = useConfig();
  const unloadBeaconSentRef = useRef(false);
  const lastRefreshTimeRef = useRef<number>(0);
  const loadDataInProgressRef = useRef(false);
  const hasLoadedInitialStateRef = useRef(false);
  
  // Account management state
  const [showAccountDialog, setShowAccountDialog] = useState(false);
  const [showClosedCalls, setShowClosedCalls] = useState(false);
  const [showShiftCommander, setShowShiftCommander] = useState(false);
  const [showAdminDialog, setShowAdminDialog] = useState(false);
  const [showMapModal, setShowMapModal] = useState(false);

  // Debug map modal state changes
  useEffect(() => {
    console.log('🗺️ DispatcherInterface: showMapModal changed to:', showMapModal);
  }, [showMapModal]);
  const [confirmDialog, setConfirmDialog] = useState<{ open: boolean; call: Call | null }>({ open: false, call: null });
  const [confirmationReason, setConfirmationReason] = useState('');
  
  // Manage unit modal state
  const [showManageUnitModal, setShowManageUnitModal] = useState(false);
  const [selectedUnitForManagement, setSelectedUnitForManagement] = useState<Unit | null>(null);
  const [unitAssignments, setUnitAssignments] = useState<UnitAssignment[]>([]);
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [loadingUnitAssignments, setLoadingUnitAssignments] = useState(false);
  const [unitTypes, setUnitTypes] = useState<UnitType[]>([]);
  const [selectedUserForAssignment, setSelectedUserForAssignment] = useState<User | null>(null);
  const [selectedRoleForAssignment, setSelectedRoleForAssignment] = useState<string>('');

  // Enhanced logout function that clears stored data, unclaims units, and closes radio windows
  const handleLogout = useCallback(async () => {
    const ensureLocalLogout = async () => {
      await logout({ skipServerRequest: true });
    };

    try {
      try {
        await requestUnitUnclaim('logout');
      } catch (error) {
        console.warn('[Logout] requestUnitUnclaim failed, continuing anyway:', error);
      }

      try {
        sendUnitUnclaimBeacon('logout');
      } catch (error) {
        console.warn('[Logout] sendUnitUnclaimBeacon failed:', error);
      }

      if ((window as any).radioAPI) {
        console.log('Closing radio window via Electron API');
        try {
          await (window as any).radioAPI.closeWindow();
        } catch (error) {
          console.error('Error closing radio window:', error);
        }
      }

      localStorage.removeItem('cad_shift_data');
      localStorage.removeItem('claimed_unit_id');

      try {
        await http.post('/auth/logout', {});
      } catch (error) {
        console.warn('[Logout] /auth/logout failed:', error);
      }

      await ensureLocalLogout();
    } catch (error) {
      console.error('Error during logout:', error);
      await ensureLocalLogout();
    } finally {
      window.location.reload();
    }
  }, [logout]);


  // Handle opening radio system
  const handleOpenRadio = useCallback(async () => {
    console.log('Dispatcher radio button clicked');
    try {
      if ((window as any).radioAPI) {
        console.log('Using Electron Radio API');
        // Get current user from auth context
        const userId = user?.username || 'user1';
        const tokenData = await (window as any).radioAPI.requestToken(userId);
        await (window as any).radioAPI.openWindow(tokenData.token);
        showSuccess('Radio system opened');
      } else {
        console.log('Opening radio in new window/tab');
        // In web browser, open radio in new window/tab
        const radioUrl = window.location.origin + '/radio?role=dispatcher';
        
        // Try to open in new window first
        const radioWindow = window.open(radioUrl, '_blank', 'width=1400,height=900,scrollbars=yes,resizable=yes');
        if (!radioWindow) {
          console.error('Failed to open radio window - popup blocked?');
          // Fallback: open in new tab
          const newTab = window.open(radioUrl, '_blank');
          if (!newTab) {
            showError('Failed to open radio window. Please allow popups or try opening manually.');
          } else {
            showSuccess('Radio system opened in new tab');
          }
        } else {
          showSuccess('Radio system opened in new window');
        }
      }
    } catch (error) {
      console.error('Failed to open radio:', error);
      showError('Failed to open radio system');
    }
  }, [user, showSuccess, showError]);


  const [calls, setCalls] = useState<Call[]>([]);
  const [units, setUnits] = useState<Unit[]>([]);
  const [stateVersion, setStateVersion] = useState<number>(0);
  const [lastStateUpdateTime, setLastStateUpdateTime] = useState<number>(0);
  const [stateRequestDebounce, setStateRequestDebounce] = useState<ReturnType<typeof setTimeout> | null>(null);
  const [isReady, setIsReady] = useState<boolean>(false);
  const [selectedCall, setSelectedCall] = useState<Call | null>(null);
  const [selectedUnits, setSelectedUnits] = useState<Set<string>>(new Set());
  const [isCallDetailsMaximized, setIsCallDetailsMaximized] = useState<boolean>(false);
  const [unitSearchQuery, setUnitSearchQuery] = useState<string>(() => {
    const saved = localStorage.getItem('dispatcherInterface_unitSearchQuery');
    return saved || '';
  });
  const [isFetching, setIsFetching] = useState(false);
  const [sortField, setSortField] = useState<string>('timeRcvdUnix');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [columnWidths, setColumnWidths] = useState({
    id: 8,
    description: 30,
    location: 25,
    units: 20,
    box: 8,
    time: 8,
    status: 8
  });
  const [isResizing, setIsResizing] = useState<string | null>(null);
  const [dragOverCallId, setDragOverCallId] = useState<string | null>(null);
  const [draggedUnitIds, setDraggedUnitIds] = useState<string[] | null>(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showCreateCallModal, setShowCreateCallModal] = useState(false);
  const [iconLoaded, setIconLoaded] = useState(false);
  
  // Track which units have claimers
  const [unitClaims, setUnitClaims] = useState<Record<string, any[]>>({});
  
  // Track if there's an active shift
  const [hasActiveShift, setHasActiveShift] = useState<boolean>(true);
  
  // Filter and grouping state with localStorage persistence
  const [selectedUnitTypes, setSelectedUnitTypes] = useState<string[]>(() => {
    const saved = localStorage.getItem('dispatcherInterface_selectedUnitTypes');
    return saved ? JSON.parse(saved) : [];
  });
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(() => {
    const saved = localStorage.getItem('dispatcherInterface_selectedStatuses');
    return saved ? JSON.parse(saved) : [];
  });
  const [groupByType, setGroupByType] = useState<boolean>(() => {
    const saved = localStorage.getItem('dispatcherInterface_groupByType');
    return saved ? JSON.parse(saved) : false;
  });
  const [groupByStatus, setGroupByStatus] = useState<boolean>(() => {
    const saved = localStorage.getItem('dispatcherInterface_groupByStatus');
    return saved ? JSON.parse(saved) : false;
  });
  const [hideOOS, setHideOOS] = useState<boolean>(() => {
    const saved = localStorage.getItem('dispatcherInterface_hideOOS');
    return saved ? JSON.parse(saved) : false;
  });
  const [showFilterDialog, setShowFilterDialog] = useState(false);

  // Context menu state
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    unitId: string;
  } | null>(null);

  // Save filter preferences to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('dispatcherInterface_selectedUnitTypes', JSON.stringify(selectedUnitTypes));
  }, [selectedUnitTypes]);

  useEffect(() => {
    localStorage.setItem('dispatcherInterface_selectedStatuses', JSON.stringify(selectedStatuses));
  }, [selectedStatuses]);

  useEffect(() => {
    localStorage.setItem('dispatcherInterface_groupByType', JSON.stringify(groupByType));
  }, [groupByType]);

  useEffect(() => {
    localStorage.setItem('dispatcherInterface_groupByStatus', JSON.stringify(groupByStatus));
  }, [groupByStatus]);

  useEffect(() => {
    localStorage.setItem('dispatcherInterface_unitSearchQuery', unitSearchQuery);
  }, [unitSearchQuery]);

  useEffect(() => {
    localStorage.setItem('dispatcherInterface_hideOOS', JSON.stringify(hideOOS));
  }, [hideOOS]);

  // Helper to robustly check if a unit is unclaimed
  const isUnclaimed = (unit: Unit) => {
    const claimers = unitClaims?.[unit.id];
    // unitClaims stores arrays of claimers
    if (Array.isArray(claimers)) {
      return claimers.length === 0;
    }
    // If no claim data exists, consider it unclaimed
    return true;
  };

  // Helper to check if a unit is a dispatch unit
  const isDispatchUnit = (unit: Unit) => 
    unit.type?.toUpperCase().includes('DISP') || unit.id === 'DISPATCH';

  // Memoized callback for checking if unit is unclaimed
  const isUnitUnclaimed = useCallback((u: Unit) => isUnclaimed(u), [unitClaims]);

  // Fetch all unit claims to update warning icons
  const refreshUnitClaims = useCallback(async () => {
    // Throttle: don't refresh more than once per second
    const now = Date.now();
    if (now - lastRefreshTimeRef.current < 1000) {
      return;
    }
    lastRefreshTimeRef.current = now;

    try {
      const claimsResponse = await http.get('/units/claims/all');
      if (claimsResponse.data?.success) {
        console.log('[DispatcherInterface] Refreshed unit claims:', claimsResponse.data.data);
        setUnitClaims(claimsResponse.data.data);
      }
    } catch (error) {
      console.error('[DispatcherInterface] Failed to refresh unit claims:', error);
    }
  }, []);

  // Get call description from call code
  const getCallDescription = (callCode: string) => {
    const codeData = callCodes[callCode];
    return codeData ? codeData.desc.toUpperCase() : callCode.toUpperCase();
  };

  // Use server-calculated status directly - no client-side derivation
  const getCallStatus = (call: any) => {
    return call.status || 'PENDING';
  };

  // Get display label for call status
  const getCallStatusLabel = (status: string) => {
    switch (status) {
      case 'PENDING': return 'PENDING';
      case 'ACTIVE': return 'ACTIVE';
      case 'PENDING_CONFIRMATION': return 'CONFIRM';
      case 'CLOSED': return 'CLOSED';
      default: return status;
    }
  };

  // Get color for call status chip
  const getCallStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING': return '#8b4513'; // Brown for pending
      case 'ACTIVE': return '#28502a'; // Green for active
      case 'PENDING_CONFIRMATION': return '#dc2626'; // Red for confirmation needed
      case 'CLOSED': return '#666666'; // Gray for closed
      default: return '#28502a';
    }
  };


  // Filter and group units
  const getFilteredAndGroupedUnits = () => {
    let filteredUnits = units.filter((unit: Unit) => {
      const matchesSearch = unit.id.toLowerCase().includes(unitSearchQuery.toLowerCase());
      const matchesType = selectedUnitTypes.length === 0 || selectedUnitTypes.includes(unit.type || '');
      const matchesStatus = selectedStatuses.length === 0 || selectedStatuses.includes(unit.status || '');
      const matchesOOS = !hideOOS || unit.status !== 'OOS';
      
      return matchesSearch && matchesType && matchesStatus && matchesOOS;
    });

    // Sort units
    filteredUnits.sort((a: Unit, b: Unit) => {
      const typeOrder = {
        'ENG': 1, 'TRK': 2, 'ALS': 3, 'RESQ': 4, 'SUPV': 5, 'BLS': 6,
        'TNKR': 7, 'BRSH': 8, 'DIV': 9, 'HELI': 10, 'OTHER': 11, 'DISP': 12
      };
      
      const aTypeOrder = typeOrder[a.type as keyof typeof typeOrder] || 999;
      const bTypeOrder = typeOrder[b.type as keyof typeof typeOrder] || 999;
      
      if (aTypeOrder !== bTypeOrder) {
        return aTypeOrder - bTypeOrder;
      }
      
      return a.id.localeCompare(b.id);
    });

    // Group units if grouping is enabled
    if (groupByType && groupByStatus) {
      const groups: Record<string, Record<string, Unit[]>> = {};
      filteredUnits.forEach((unit: Unit) => {
        const type = unit.type || 'OTHER';
        const status = unit.status || 'UNKNOWN';
        if (!groups[type]) groups[type] = {};
        if (!groups[type][status]) groups[type][status] = [];
        groups[type][status].push(unit);
      });
      return groups;
    } else if (groupByType) {
      const groups: Record<string, Unit[]> = {};
      filteredUnits.forEach((unit: Unit) => {
        const type = unit.type || 'OTHER';
        if (!groups[type]) groups[type] = [];
        groups[type].push(unit);
      });
      return groups;
    } else if (groupByStatus) {
      const groups: Record<string, Unit[]> = {};
      filteredUnits.forEach((unit: Unit) => {
        const status = unit.status || 'UNKNOWN';
        if (!groups[status]) groups[status] = [];
        groups[status].push(unit);
      });
      return groups;
    }

    return filteredUnits;
  };

  // Handle column sorting
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Sort calls based on current sort settings
  const getSortedCalls = (calls: Call[]) => {
    return [...calls].sort((a, b) => {
      let aValue: any, bValue: any;
      
      switch (sortField) {
        case 'id':
          aValue = a.id;
          bValue = b.id;
          break;
        case 'callCode':
          aValue = getCallDescription(a.callCode);
          bValue = getCallDescription(b.callCode);
          break;
        case 'location':
          aValue = a.location.toUpperCase();
          bValue = b.location.toUpperCase();
          break;
        case 'units':
          aValue = a.units ? Object.keys(a.units).length : 0;
          bValue = b.units ? Object.keys(b.units).length : 0;
          break;
        case 'boxArea':
          aValue = a.boxArea || '';
          bValue = b.boxArea || '';
          break;
        case 'timeRcvdUnix':
        default:
          aValue = a.timeRcvdUnix || 0;
          bValue = b.timeRcvdUnix || 0;
          break;
      }
      
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Handle column resizing
  const handleMouseDown = (column: string, e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(column);
    
    const startX = e.clientX;
    const startWidth = columnWidths[column as keyof typeof columnWidths];
    
    const handleMouseMove = (e: MouseEvent) => {
      // Different constraints for different column types (in percentage)
      let minWidth = 5; // 5%
      let maxWidth = 20; // 20%
      
      if (['description', 'location', 'units'].includes(column)) {
        minWidth = 15; // 15%
        maxWidth = 40; // 40%
      } else if (['id', 'time', 'status', 'box'].includes(column)) {
        minWidth = 5; // 5%
        maxWidth = 15; // 15%
      }
      
      const deltaX = e.clientX - startX;
      const deltaPercent = (deltaX / window.innerWidth) * 100; // Convert pixels to percentage
      const newWidth = Math.max(minWidth, Math.min(maxWidth, startWidth + deltaPercent));
      
      setColumnWidths((prev: typeof columnWidths) => ({
        ...prev,
        [column]: newWidth
      }));
    };
    
    const handleMouseUp = () => {
      setIsResizing(null);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Timer effect to update call timers every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Validate authentication and log out if invalid
  const validateTokenAndLogout = useCallback(async () => {
    // Check for authentication cookie (cookie-based auth)
    const isAuthCookie = document.cookie.includes('cad_authenticated=true') || 
                         document.cookie.includes('__Host-cad_authenticated=true');
    
    // If no cookie and no user, log out
    if (!isAuthCookie && !user) {
      console.log('No authentication found, logging out');
      await logout();
      return false;
    }

    // If we have a user, we're authenticated
    if (user) {
      return true;
    }

    try {
      // Verify with server (cookie will be sent automatically)
      const response = await apiClient.post('/auth/verify-cookie', {});
      
      if (!response.success) {
        console.log('Authentication validation failed, logging out');
        await logout();
        return false;
      }
      
      return true;
    } catch (error: any) {
      // Don't log out on network errors or rate limiting
      if (error.response?.status === 429 || error.code === 'NETWORK_ERROR') {
        console.log('Network error or rate limit during validation, skipping logout');
        return true;
      }
      
      console.log('Authentication validation error, logging out:', error);
      await logout();
      return false;
    }
  }, [user, logout]);

  // Icon will be preloaded by the bundler automatically

  // Periodic token validation - check every 5 minutes
  useEffect(() => {
    const interval = setInterval(async () => {
      await validateTokenAndLogout();
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(interval);
  }, [validateTokenAndLogout]);



  // Cleanup drag state when component unmounts
  useEffect(() => {
    return () => {
      setDragOverCallId(null);
      setDraggedUnitIds(null);
    };
  }, []);

  // Reset dragged unit IDs when drag ends
  useEffect(() => {
    const handleDragEnd = () => {
      setDraggedUnitIds(null);
      setDragOverCallId(null);
    };

    document.addEventListener('dragend', handleDragEnd);
    return () => {
      document.removeEventListener('dragend', handleDragEnd);
    };
  }, []);


  // Use ref for version tracking to avoid stale closures
  const stateVersionRef = React.useRef<number>(0);
  const previousCallIdsRef = React.useRef<Set<string>>(new Set());

  // State update handler with version monotonicity validation
  const handleStateUpdate = useCallback((data: any) => {
    const incomingVersion = data.version || 0;
    const currentVersion = stateVersionRef.current;
    
    // Version monotonicity validation - more lenient approach
    // Allow same version (idempotent updates) and newer versions
    if (incomingVersion < currentVersion && currentVersion > 0) {
      console.warn(`[State Update] Ignoring stale update: received version ${incomingVersion}, current version ${currentVersion}`);
      return;
    }

    // Log version gaps for debugging but don't block processing
    if (incomingVersion > currentVersion + 1 && currentVersion > 0) {
      console.warn(`[State Update] Version gap detected: expected ${currentVersion + 1}, received ${incomingVersion} (allowing anyway)`);
    }

    console.log(`[State Update] Processing authoritative state update version ${incomingVersion} (prev: ${currentVersion})`);
    
    // Update version ref immediately to prevent duplicate processing
    stateVersionRef.current = incomingVersion;
    
    // Update the last state update timestamp
    setLastStateUpdateTime(Date.now());

    // Batch state updates to avoid multiple re-renders
    if (data.calls) {
      const callsArray = Array.isArray(data.calls) ? data.calls : Object.values(data.calls);
      console.log(`[State Update] Received ${callsArray.length} active calls`);
      
      // Detect new calls for audio notification
      const currentCallIds = new Set<string>(callsArray.map((call: Call) => call.id));
      const previousCallIds = previousCallIdsRef.current;
      
      // Find new calls (exist in current but not in previous)
      const newCallIds = Array.from(currentCallIds).filter((id: string) => !previousCallIds.has(id));
      
      if (newCallIds.length > 0 && previousCallIds.size > 0) {
        // Play audio for new calls after initial load
        console.log(`[Audio] Detected ${newCallIds.length} new call(s):`, newCallIds);
        
        // Find the new calls to check if any are PENDING
        const newCalls = callsArray.filter((call: Call) => newCallIds.includes(call.id));
        const newPendingCalls = newCalls.filter((call: Call) => call.status === 'PENDING');
        
        // Play audio for new PENDING calls
        if (newPendingCalls.length > 0) {
          console.log(`[Audio] Found ${newPendingCalls.length} new PENDING call(s), playing new incident sound`);
          audioNotifications.playNewWaitingIncident();
        } else {
          console.log(`[Audio] New calls exist but none are PENDING, no audio played`);
        }
      } else if (newCallIds.length > 0) {
        // Initial load - play audio once if there are any PENDING calls
        const pendingCalls = callsArray.filter((call: Call) => call.status === 'PENDING');
        if (pendingCalls.length > 0) {
          console.log(`[Audio] Initial load with ${pendingCalls.length} PENDING call(s), playing new incident sound once`);
          audioNotifications.playNewWaitingIncident();
        } else {
          console.log(`[Audio] Initial load with ${newCallIds.length} call(s) but none are PENDING, no audio played`);
        }
      }
      
      // Update the previous call IDs for next comparison
      previousCallIdsRef.current = currentCallIds;
      
      // Always update calls when we get a new version - state-update is authoritative
      setCalls(callsArray);
      
      // Only update selected call if it exists and is still active
      setSelectedCall((prevSelected: Call | null) => {
        if (!prevSelected) return null;
        const updatedSelectedCall = callsArray.find((call: Call) => 
          call.id === prevSelected.id && call.status !== 'CLOSED'
        );
        if (updatedSelectedCall) {
          return updatedSelectedCall;
        } else {
          // Call was closed or removed - clear selection and minimize
          console.log(`[State Update] Selected call ${prevSelected.id} is no longer active, clearing selection`);
          setIsCallDetailsMaximized(false);
          return null;
        }
      });
    }
    
    if (data.units) {
      const unitsArray = Array.isArray(data.units) ? data.units : Object.values(data.units);
      
      // Always update units when we get a new version - state-update is authoritative
      setUnits(unitsArray);
      
      // Update unitClaims state based on unit claimers
      // Note: Socket events provide more real-time claimer data, so we preserve that
      // Only initialize units that don't have claim data yet
      setUnitClaims(prevClaims => {
        const newUnitClaims: Record<string, any[]> = { ...prevClaims };
        unitsArray.forEach((unit: any) => {
          // Only initialize if we don't have claim data from socket events
          if (!prevClaims[unit.id]) {
            newUnitClaims[unit.id] = [];
          }
          // Otherwise, keep the existing socket data (more real-time)
        });
        return newUnitClaims;
      });
    }
    
    // Update version state (for display/debugging)
    setStateVersion(incomingVersion);

    // Mark as ready after first successful state update
    setIsReady((prevReady: boolean) => {
      if (!prevReady) {
        console.log(`[State Update] System ready with version ${incomingVersion}`);
        return true;
      }
      return prevReady;
    });

  }, []); // No dependencies - uses ref for version

  // Socket event handlers
  // Debounced state request function
  const requestStateDebounced = useCallback(() => {
    // Clear any existing debounce timer
    if (stateRequestDebounce) {
      clearTimeout(stateRequestDebounce);
    }
    
    // Set a new debounce timer
    const timer = setTimeout(async () => {
      const timeSinceLastUpdate = Date.now() - (lastStateUpdateTime || 0);
      const shouldResync = !isReady || timeSinceLastUpdate > 30000; // 30 seconds without updates
      
      if (shouldResync) {
        console.log('[Socket] Debounced state resync...');
        try {
          const response = await apiClient.getState();
          if (response.success && response.data) {
            console.log(`[Socket] Resynced state version ${response.data.version}`);
            handleStateUpdate(response.data);
          }
        } catch (error) {
          console.error('[Socket] Failed to resync state:', error);
        }
      } else {
        console.log('[Socket] Recent state available, skipping debounced resync');
      }
    }, 2000); // 2 second debounce
    
    setStateRequestDebounce(timer);
  }, [isReady, lastStateUpdateTime, stateRequestDebounce, handleStateUpdate]);

  useEffect(() => {
    if (!socket) {
      console.log('Socket not available yet, skipping event handlers');
      return;
    }

    // Handle socket reconnection and state resync
    const handleConnect = () => {
      console.log('[Socket] Connected');
      requestStateDebounced();
    };

    const handleDisconnect = () => {
      console.log('[Socket] Disconnected');
    };

    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);

    const handleCallCreated = (newCall: Call) => {
      // Let state-update handle the actual state change to prevent version conflicts
      console.log(`[Socket] Call created event received:`, newCall);
    };

    const handleCallUpdated = (updatedCall: Call) => {
      // Let state-update handle the actual state change to prevent version conflicts
      console.log(`[Socket] Call updated event received:`, updatedCall);
    };

    const handleUnitUpdated = (updatedUnit: Unit) => {
      // Let state-update handle the actual state change to prevent version conflicts
      console.log(`[Socket] Unit updated event received:`, updatedUnit);
    };

    const handleCommentAdded = (data: { callId: string; comment: any }) => {
      // Comments are now handled by authoritative state-update events
      // This handler is kept for backward compatibility but doesn't update state
      console.log(`[Comment] Comment added to call ${data.callId}, waiting for state-update`);
    };

    // Handle real-time unit claim updates from socket
    const handleUnitClaimUpdated = (data: any) => {
      console.log('Received unit claim update:', data);
      
      setUnitClaims((prevClaims: Record<string, any[]>) => {
        const newClaims = {
          ...prevClaims,
          [data.unitId]: data.claimedBy || []
        };
        console.log(`[DispatcherInterface] Updated unitClaims for ${data.unitId}: ${data.claimedBy?.length || 0} claimers. New state:`, newClaims);
        return newClaims;
      });
      
      // Show toast notification for claim changes
      if (data.isClaimed && data.claimedBy && data.claimedBy.length > 0) {
        const claimerNames = data.claimedBy.map((c: any) => c.username).join(', ');
        showInfo(`${claimerNames} ${data.claimedBy.length > 1 ? 'claimed' : 'claims'} unit ${data.unitId}`);
      } else if (!data.isClaimed && data.previousClaimer) {
        showInfo(`${data.previousClaimer.username} unclaimed unit ${data.unitId}`);
      }
    };

    socket.on('call-created', handleCallCreated);
    socket.on('call-updated', handleCallUpdated);
    socket.on('unit-updated', handleUnitUpdated);
    socket.on('unit-assigned', () => {
      // no-op; state will arrive via 'state-update'
    });
    socket.on('state-update', handleStateUpdate);
    socket.on('comment-added', handleCommentAdded);
    socket.on('unit-claim-updated', handleUnitClaimUpdated);
    
    socket.on('unit-assignment-updated', handleAssignmentUpdate);
    socket.on('unit-assignment-removed', handleAssignmentUpdate);

    return () => {
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      socket.off('call-created', handleCallCreated);
      socket.off('call-updated', handleCallUpdated);
      socket.off('unit-updated', handleUnitUpdated);
      socket.off('unit-assigned');
      socket.off('state-update', handleStateUpdate);
      socket.off('comment-added', handleCommentAdded);
      socket.off('unit-claim-updated', handleUnitClaimUpdated);
      socket.off('unit-assignment-updated', handleAssignmentUpdate);
      socket.off('unit-assignment-removed', handleAssignmentUpdate);
      
      // Cleanup debounce timer
      if (stateRequestDebounce) {
        clearTimeout(stateRequestDebounce);
      }
    };
  }, [socket, handleStateUpdate, showInfo, requestStateDebounced]);

  // Refresh unit claims periodically and on socket reconnection
  useEffect(() => {
    if (!socket) return;

    // Only refresh on actual socket connect events, not on every effect run
    const handleConnect = () => {
      console.log('[DispatcherInterface] Socket connected, refreshing unit claims');
      refreshUnitClaims();
    };

    // Only set up listeners if socket is already connected
    if (socket.connected) {
      refreshUnitClaims();
    }

    socket.on('connect', handleConnect);

    // Periodic refresh every 30 seconds as a backup to socket events
    const intervalId = setInterval(() => {
      refreshUnitClaims();
    }, 30000);

    return () => {
      socket.off('connect', handleConnect);
      clearInterval(intervalId);
    };
  }, [socket]); // Removed refreshUnitClaims from deps - it's stable

  const loadData = async (opts?: { silent?: boolean }) => {
    // Prevent multiple simultaneous calls
    if (loadDataInProgressRef.current) {
      console.log('[Load Data] Already in progress, skipping duplicate call');
      return;
    }
    
    const silent = opts?.silent ?? false;
    loadDataInProgressRef.current = true;
    try {
      if (!silent) setIsFetching(true);
      
      // Use state endpoint for authoritative initial load
      const stateResponse = await apiClient.getState();
      if (stateResponse.success && stateResponse.data) {
        console.log(`[Load Data] Loaded authoritative state version ${stateResponse.data.version}`);
        handleStateUpdate(stateResponse.data);
      } else if (stateResponse.error?.includes('throttled')) {
        // Don't fallback on throttling - wait for socket events or next retry
        console.log('[Load Data] State request throttled, skipping fallback - will use socket events');
        // State will come via socket 'state-update' events
      } else {
        // Only fallback to individual endpoints for actual failures (not throttling)
        console.log('[Load Data] State request failed, falling back to individual endpoints');
        const [callsResponse, unitsResponse] = await Promise.all([
          apiClient.getCalls(),
          apiClient.getUnits()
        ]);
        
        // Handle API response format - extract data from success response
        const callsData = callsResponse.success && callsResponse.data ? callsResponse.data : [];
        const unitsData = unitsResponse.success && unitsResponse.data ? unitsResponse.data : [];
        
        // Ensure we have arrays
        setCalls(Array.isArray(callsData) ? callsData : []);
        setUnits(Array.isArray(unitsData) ? unitsData : []);
      }

      // Unit claims are loaded separately via refreshUnitClaims to avoid duplicate calls
      // This is handled by the useEffect that sets up socket listeners and periodic refresh
    } catch (error) {
      console.error('Failed to load data:', error);
      // Don't clear state on error - keep existing state and wait for socket updates
      // setCalls([]);
      // setUnits([]);
    } finally {
      if (!silent) setIsFetching(false);
      loadDataInProgressRef.current = false;
    }
  };

  // Load initial data once when authenticated and socket is connected
  useEffect(() => {
    // Only run once after we're authenticated AND socket is connected
    if (!user) return;
    if (!socket || !socket.connected) return;
    
    if (hasLoadedInitialStateRef.current) {
      return;
    }
    hasLoadedInitialStateRef.current = true;
    
    console.log('[Load Data] Running initial state load once...');
    loadData();
  }, [user, socket]); // Only depend on user and socket, not loadData

  // Wire tab-close unclaim (separate effect, no dependencies)
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (unloadBeaconSentRef.current) return;
      unloadBeaconSentRef.current = true;
      sendUnitUnclaimBeacon('window-unload');
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    const handlePageHide = (event: PageTransitionEvent) => {
      if (event.persisted) return;
      handleBeforeUnload();
    };
    window.addEventListener('pagehide', handlePageHide);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, []); // Empty deps - only run once on mount

  const handleCreateCall = async (callData: any) => {
    // The call has already been created by CreateCallModal
    // This function just handles the callback
    console.log('Call created successfully:', callData);
    setShowCreateCallModal(false);
    // Socket will handle the data update automatically
  };

  const handleAttachUnits = async (unitIds: string[]) => {
    if (!selectedCall) {
      showError('No call selected');
      return;
    }
    
    // Check if call is closed
    if (selectedCall.status === 'CLOSED') {
      showWarning(`Cannot assign units to closed call ${selectedCall.id}`);
      return;
    }
    
    if (unitIds.length === 0) {
      showWarning('No units selected');
      return;
    }
    
    // Validate and filter units
    const alreadyAssigned: string[] = [];
    const invalidStatus: string[] = [];
    const validUnits: string[] = [];
    
    unitIds.forEach((unitId: string) => {
      const unit = units.find((u: Unit) => u.id === unitId);
      
      if (unit && unit.assignedCall === selectedCall.id) {
        alreadyAssigned.push(unitId);
      } else if (unit && unit.status !== 'I/S') {
        invalidStatus.push(unitId);
      } else {
        validUnits.push(unitId);
      }
    });
    
    // Show warnings for invalid units
    if (alreadyAssigned.length > 0) {
      showWarning(`Units already assigned: ${alreadyAssigned.join(', ')}`);
    }
    
    if (invalidStatus.length > 0) {
      showWarning(`Units must be I/S to assign: ${invalidStatus.join(', ')}`);
    }
    
    // If no valid units, return
    if (validUnits.length === 0) {
      return;
    }
    
    // Proceed with valid units
    try {
      const response = await http.post('/calls/assign', {
        callId: selectedCall.id,
        unitIds: validUnits
      });
      
      if (response.status === 200) {
        setSelectedUnits(new Set());
        showSuccess(`Assigned ${validUnits.length} unit(s) to call ${selectedCall.id}`);
        // State will be updated via authoritative 'state-update' event from server
      } else {
        showError('Failed to assign units to call');
      }
    } catch (error) {
      console.error('Failed to assign units to call:', error);
      showError('Failed to assign units to call');
    }
  };


  const handleUnitSelect = (unitId: string, checked: boolean) => {
    const newSelected = new Set(selectedUnits);
    if (checked) {
      newSelected.add(unitId);
    } else {
      newSelected.delete(unitId);
    }
    setSelectedUnits(newSelected);
  };


  const handleUnitStatusChange = async (unitId: string, newStatus: string) => {
    try {
      const response = await apiClient.updateUnitStatus(unitId, newStatus);
      
      if (!response.success) {
        console.error('Failed to update unit status:', response.error);
        showError(`Failed to update unit ${unitId} status: ${response.error}`);
      }
      // State will be updated via authoritative 'state-update' event from server
    } catch (error) {
      console.error('Error updating unit status:', error);
      showError(`Failed to update unit ${unitId} status`);
    }
  };

  // Handle context menu for unit rows
  const handleContextMenu = (event: React.MouseEvent, unitId: string) => {
    event.preventDefault();
    setContextMenu(
      contextMenu === null
        ? {
            mouseX: event.clientX + 2,
            mouseY: event.clientY - 6,
            unitId: unitId,
          }
        : null,
    );
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  // Handle manage unit
  const handleManageUnit = async (unitId: string) => {
    const unit = units.find((u: Unit) => u.id === unitId);
    if (unit) {
      setSelectedUnitForManagement(unit);
      setShowManageUnitModal(true);
      // Reset form state
      setSelectedUserForAssignment(null);
      setSelectedRoleForAssignment('');
      await loadUnitAssignments(unitId);
    }
    setContextMenu(null);
  };

  // Handle closing manage unit modal
  const handleCloseManageUnitModal = () => {
    setShowManageUnitModal(false);
    setSelectedUnitForManagement(null);
    setSelectedUserForAssignment(null);
    setSelectedRoleForAssignment('');
  };

  // Load unit assignments for the manage unit modal
  const loadUnitAssignments = useCallback(async (unitId: string) => {
    try {
      setLoadingUnitAssignments(true);
      
      // First try to load from big board data (current shift)
      const response = await http.get('/unit-assignments/big-board');
      let assignments: any[] = [];
      
      if (response.data.success) {
        const bigBoard = response.data.data;
        
        // If there's an active shift, use the big board data (shift assignments)
        if (bigBoard.shift) {
          setHasActiveShift(true);
          const unitWithAssignments = bigBoard.units.find((u: any) => u.id === unitId);
          assignments = unitWithAssignments?.assignments || [];
        } else {
          // No active shift - get current unit claimers
          setHasActiveShift(false);
          console.log('[ManageUnit] No active shift, loading current unit claimers for unit:', unitId);
          
          // Get current unit claims
          const claimsResponse = await http.get(`/units/${unitId}/claims`);
          if (claimsResponse.data.success && claimsResponse.data.data.claimedBy) {
            // Convert claimers to assignment-like format for display
            assignments = claimsResponse.data.data.claimedBy.map((claimer: any) => ({
              id: `claim-${claimer.id}`,
              unitId: unitId,
              userId: claimer.id,
              role: 'Claimer',
              personnelName: claimer.name || claimer.username,
              callsign: claimer.username,
              isClaim: true // Flag to distinguish from shift assignments
            }));
          }
        }
      }
      
      setUnitAssignments(assignments);
      
      // Load available users
      const usersResponse = await http.get('/unit-assignments/users');
      if (usersResponse.data.success) {
        setAvailableUsers(usersResponse.data.data);
      }

      // Load unit types for role selection
      const unitTypesResponse = await http.get('/unit-assignments/unit-types');
      if (unitTypesResponse.data.success) {
        setUnitTypes(unitTypesResponse.data.data);
      }
    } catch (error: any) {
      console.error('Error loading unit assignments:', error);
      showError('Failed to load unit assignments');
    } finally {
      setLoadingUnitAssignments(false);
    }
  }, [showError]);

  // Handle unit assignment updates for manage unit modal
  const handleAssignmentUpdate = useCallback(() => {
    if (selectedUnitForManagement) {
      loadUnitAssignments(selectedUnitForManagement.id);
    }
  }, [selectedUnitForManagement, loadUnitAssignments]);

  // Get available roles for the selected unit
  const getAvailableRolesForUnit = useCallback(() => {
    if (!selectedUnitForManagement || !unitTypes.length) return [];
    
    const unitType = unitTypes.find(ut => ut.id === selectedUnitForManagement.type);
    if (!unitType) return [];
    
    return [...unitType.requiredRoles, ...unitType.optionalRoles];
  }, [selectedUnitForManagement, unitTypes]);

  // Add user to unit assignment (used by quick add chips)
  const handleAddUserToUnit = async (userId: string, role: string = 'Officer') => {
    if (!selectedUnitForManagement) return;
    
    try {
      // Get current shift from big board data
      const response = await http.get('/unit-assignments/big-board');
      if (!response.data.success || !response.data.data.shift) {
        showError('No active shift found. Please start a shift before assigning users to units.');
        return;
      }
      
      const shiftId = response.data.data.shift.id;
      
      // Create assignment
      await http.post('/unit-assignments', {
        shiftId,
        unitId: selectedUnitForManagement.id,
        userId,
        role,
        isPrimary: false
      });
      
      showSuccess('User added to unit assignment');
      await loadUnitAssignments(selectedUnitForManagement.id);
    } catch (error: any) {
      console.error('Error adding user to unit:', error);
      showError(error?.response?.data?.error || 'Failed to add user to unit');
    }
  };

  // Handle form-based user assignment
  const handleFormUserAssignment = async () => {
    if (!selectedUnitForManagement || !selectedUserForAssignment) {
      showError('Please select a user');
      return;
    }
    
    try {
      if (hasActiveShift) {
        // Normal assignment flow for active shifts
        if (!selectedRoleForAssignment) {
          showError('Please select a role');
          return;
        }
        await handleAddUserToUnit(selectedUserForAssignment.id, selectedRoleForAssignment);
      } else {
        // Claim management for no active shift
        await handleAssignUnitToUser(selectedUserForAssignment.id);
      }
      
      // Clear the form
      setSelectedUserForAssignment(null);
      setSelectedRoleForAssignment('');
    } catch (error: any) {
      console.error('Error in form assignment:', error);
    }
  };

  // Assign unit to user (for claim management when no shift is active)
  const handleAssignUnitToUser = async (userId: string) => {
    if (!selectedUnitForManagement) return;
    
    try {
      await http.post(`/units/${selectedUnitForManagement.id}/assign-to-user`, {
        userId: userId
      });
      
      showSuccess('Unit assigned to user');
      await loadUnitAssignments(selectedUnitForManagement.id);
    } catch (error: any) {
      console.error('Error assigning unit to user:', error);
      showError(error?.response?.data?.error || 'Failed to assign unit to user');
    }
  };

  // Unclaim unit from user
  const handleUnclaimUnit = async () => {
    if (!selectedUnitForManagement) return;
    
    try {
      await http.post(`/units/${selectedUnitForManagement.id}/clear-claims`);
      
      showSuccess('Unit unclaimed from user');
      await loadUnitAssignments(selectedUnitForManagement.id);
    } catch (error: any) {
      console.error('Error unclaiming unit:', error);
      showError(error?.response?.data?.error || 'Failed to unclaim unit');
    }
  };

  // Remove user from unit assignment
  const handleRemoveUserFromUnit = async (assignmentId: string) => {
    try {
      await http.delete(`/unit-assignments/${assignmentId}`);
      showSuccess('User removed from unit assignment');
      
      if (selectedUnitForManagement) {
        await loadUnitAssignments(selectedUnitForManagement.id);
      }
    } catch (error: any) {
      console.error('Error removing user from unit:', error);
      showError(error?.response?.data?.error || 'Failed to remove user from unit');
    }
  };

  // Get dispatch unit status for header buttons
  const dispatchUnit = units.find((unit: Unit) => unit.id === 'DISPATCH');
  const dispatchStatus = dispatchUnit?.status || 'I/S';

  const getStatusColor = (status: string) => {
    // Using exact colors from Config.lua UNIT_COLOR
    switch (status) {
      case 'I/S': return '#32be5a'; // Color3.fromRGB(50,190,90)
      case 'DISP': return '#ffd25a'; // Color3.fromRGB(255,210,90)
      case 'E/R': return '#f0b432'; // Color3.fromRGB(240,180,50)
      case 'O/S': return '#3c96ff'; // Color3.fromRGB(60,150,255)
      case 'TRX': return '#c896ff'; // Color3.fromRGB(200,150,255)
      case 'TRX ARR': return '#b478f0'; // Color3.fromRGB(180,120,240)
      case 'OOS': return '#a0a0a0'; // Color3.fromRGB(160,160,160)
      case 'OOS-GAME': return '#787878'; // Color3.fromRGB(120,120,120)
      default: return '#a0a0a0';
    }
  };


  // Handle call confirmation dialog
  const handleCallConfirmation = (call: Call) => {
    setConfirmDialog({ open: true, call });
    setConfirmationReason('');
  };

  const handleConfirmCallStatus = async (action: 'pending' | 'closed') => {
    if (!confirmDialog.call) return;
    
    try {
      const response = await http.post(`/calls/${confirmDialog.call.id}/confirm-status`, {
        action,
        reason: confirmationReason || undefined
      });
      
      if (response?.data?.success) {
        showSuccess('Call status confirmed successfully');
        // Refresh the calls data
        requestStateDebounced();
      } else {
        showError(response?.data?.error || 'Failed to confirm call status');
      }
    } catch (error) {
      console.error('Error confirming call status:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      showError('Error confirming call status: ' + errorMessage);
    }
    
    setConfirmDialog({ open: false, call: null });
    setConfirmationReason('');
  };

  if (!isReady) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" flexDirection="column" gap={2}>
        <CircularProgress />
        <Typography sx={{ color: '#eceef1' }}>
          {!socket ? 'Connecting to CAD server...' : 'Loading authoritative state...'}
        </Typography>
        {stateVersion > 0 && (
          <Typography sx={{ color: '#666', fontSize: 12 }}>
            State version: {stateVersion}
          </Typography>
        )}
      </Box>
    );
  }

  // Show connection status if socket is not available
  if (!socket) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" flexDirection="column" gap={2}>
        <CircularProgress />
        <Typography sx={{ color: '#eceef1' }}>
          Connecting to CAD server...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100vh', width: '100vw', display: 'flex', flexDirection: 'column', backgroundColor: '#101216', overflow: 'hidden' }}>
      <DispatcherHeader
        iconLoaded={iconLoaded}
        setIconLoaded={setIconLoaded}
        dispatchStatus={dispatchStatus}
        currentTime={currentTime}
        isFetching={isFetching}
        onShowAccountDialog={() => setShowAccountDialog(true)}
        onShowClosedCalls={() => setShowClosedCalls(true)}
        onShowShiftCommander={() => setShowShiftCommander(true)}
        onShowAdminDialog={() => setShowAdminDialog(true)}
        onShowRadio={handleOpenRadio}
        onLogout={handleLogout}
        onReturnToShiftSelection={onReturnToShiftSelection}
      />


      {/* Main Content - matching screenshot layout exactly */}
      <Box sx={{ flex: 1, display: 'flex', padding: '16px 16px 16px 0', overflow: 'hidden', minHeight: 0, gap: '16px' }}>
        {/* Left Panel - Units (responsive width) */}
        <Paper
          sx={{
            width: '20vw', // 20% of viewport width
            minWidth: '250px',
            maxWidth: '300px',
            backgroundColor: '#1c1f25', // surface
            border: '1px solid #40444b', // border
            borderRadius: 1.5,
            padding: 1.25,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}
        >
          {/* Units Header */}
          <Box sx={{ marginBottom: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 1 }}>
              <Typography variant="h6" sx={{ color: '#eceef1', fontSize: 16, fontWeight: 'bold', flexGrow: 1 }}>
              UNITS
            </Typography>
              <IconButton
                onClick={() => setShowFilterDialog(true)}
                sx={{ color: '#eceef1', marginRight: 1 }}
                title="Filter Units"
              >
                <FilterIcon />
              </IconButton>
            <Button
                variant={hideOOS ? "contained" : "outlined"}
                onClick={() => setHideOOS(!hideOOS)}
              size="small"
              sx={{
                  backgroundColor: hideOOS ? '#3c8cff' : 'transparent',
                  color: hideOOS ? '#0a0c0e' : '#eceef1',
                  borderColor: '#464a52',
                  fontSize: 11,
                minWidth: 60,
                  height: 28,
                  marginRight: 1,
                  '&:hover': {
                    backgroundColor: hideOOS ? '#2a7ae6' : '#2a2a2a',
                    borderColor: '#3c8cff'
                  }
                }}
                title={hideOOS ? "Show OOS Units" : "Hide OOS Units"}
              >
                HIDE OOS
            </Button>
            <IconButton
              sx={{ color: '#eceef1' }}
              title="Map View"
              onClick={() => {
                console.log('🗺️ Map button clicked, opening map modal...');
                setShowMapModal(true);
              }}
            >
              <MapIcon />
            </IconButton>
            </Box>
          </Box>

            {/* Search Box */}
            <TextField
              fullWidth
              size="small"
              placeholder="Search units..."
              value={unitSearchQuery}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUnitSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#eceef1' }} />
                  </InputAdornment>
                ),
                endAdornment: unitSearchQuery && (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setUnitSearchQuery('')}
                      size="small"
                      sx={{ color: '#eceef1' }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                marginBottom: 1,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#1a1d22',
                  color: '#eceef1',
                  '& fieldset': {
                    borderColor: '#464a52',
                  },
                  '&:hover fieldset': {
                    borderColor: '#3c8cff',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#3c8cff',
                  },
                },
                '& .MuiInputBase-input': {
                  color: '#eceef1',
                  '&::placeholder': {
                    color: '#9ca3af',
                    opacity: 1,
                  },
                },
              }}
            />


          {/* Units List */}
          <Box sx={{ 
            flex: 1, 
            overflowY: 'scroll',
            overflowX: 'hidden',
            minHeight: 0,
            paddingRight: 1,
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#1a1d22',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#464a52',
              borderRadius: '4px',
              '&:hover': {
                background: '#5a5e66',
              },
            },
            // Always show vertical scrollbar, even when content doesn't overflow
            scrollbarWidth: 'thin',
            scrollbarColor: '#464a52 #1a1d22',
          }}>
            {(() => {
              const filteredAndGroupedUnits = getFilteredAndGroupedUnits();
              
              // Render grouped units
              if (groupByType || groupByStatus) {
                return Object.entries(filteredAndGroupedUnits as Record<string, any>).map(([groupKey, groupValue]) => (
                  <Box key={groupKey} sx={{ marginBottom: 1.5 }}>
                    {/* Group Header */}
                    <Typography sx={{ 
                      color: '#3c8cff', 
                      fontSize: 14, 
                      fontWeight: 'bold', 
                      marginBottom: 0.5,
                      paddingLeft: 1,
                      borderLeft: '3px solid #3c8cff'
                    }}>
                      {groupKey} ({Array.isArray(groupValue) ? groupValue.length : Object.values(groupValue).flat().length})
                    </Typography>
                    
                    {/* Group Units */}
                    {Array.isArray(groupValue) ? (
                      groupValue.map((unit: Unit) => (
                        <Box
                          key={unit.id}
                          draggable={unit.id !== 'DISPATCH'}
                          onContextMenu={(e: React.MouseEvent) => handleContextMenu(e, unit.id)}
                          onDragStart={(e: React.DragEvent) => {
                            // If this unit is selected, drag all selected units; otherwise just this one
                            const unitsToDrag = selectedUnits.has(unit.id) && selectedUnits.size > 0 
                              ? Array.from(selectedUnits) 
                              : [unit.id];
                            e.dataTransfer.setData('text/plain', JSON.stringify(unitsToDrag));
                            e.dataTransfer.effectAllowed = 'copy';
                            setDraggedUnitIds(unitsToDrag);
                            
                            // Create custom drag image with stacked unit rows
                            if (unitsToDrag.length > 1) {
                              const dragContainer = document.createElement('div');
                              dragContainer.style.cssText = 'position: absolute; top: -10000px; left: -10000px; width: 280px;';
                              
                              (unitsToDrag.slice(0, 5) as string[]).forEach((unitId: string, index: number) => {
                                const dragUnit = units.find((u: Unit) => u.id === unitId);
                                if (dragUnit) {
                                  const unitRow = document.createElement('div');
                                  unitRow.style.cssText = `
                                    background: ${getStatusColor(dragUnit.status)};
                                    border: 2px solid #3c8cff;
                                    border-radius: 4px;
                                    padding: 6px 12px;
                                    margin-bottom: ${index < Math.min(unitsToDrag.length, 5) - 1 ? '4px' : '0'};
                                    display: flex;
                                    align-items: center;
                                    gap: 8px;
                                    min-height: 40px;
                                    box-shadow: 0 2px 8px rgba(0,0,0,0.3);
                                  `;
                                  
                                  const unitChip = document.createElement('span');
                                  unitChip.style.cssText = 'background: #2a2d32; color: #eceef1; padding: 4px 12px; border-radius: 4px; font-weight: bold; font-size: 14px; font-family: system-ui, -apple-system, sans-serif;';
                                  unitChip.textContent = unitId;
                                  
                                  const statusChip = document.createElement('span');
                                  statusChip.style.cssText = 'background: #1a1d22; color: #eceef1; padding: 4px 12px; border-radius: 4px; font-weight: bold; font-size: 12px; margin-left: auto; font-family: system-ui, -apple-system, sans-serif;';
                                  statusChip.textContent = dragUnit.status || 'I/S';
                                  
                                  unitRow.appendChild(unitChip);
                                  unitRow.appendChild(statusChip);
                                  dragContainer.appendChild(unitRow);
                                }
                              });
                              
                              // Add "+X more" indicator if there are more than 5 units
                              if (unitsToDrag.length > 5) {
                                const moreIndicator = document.createElement('div');
                                moreIndicator.style.cssText = 'background: #3c8cff; color: white; padding: 6px 12px; border-radius: 4px; text-align: center; font-weight: bold; font-size: 12px; margin-top: 4px; font-family: system-ui, -apple-system, sans-serif;';
                                moreIndicator.textContent = `+${unitsToDrag.length - 5} more`;
                                dragContainer.appendChild(moreIndicator);
                              }
                              
                              document.body.appendChild(dragContainer);
                              e.dataTransfer.setDragImage(dragContainer, 140, 20);
                              setTimeout(() => document.body.removeChild(dragContainer), 0);
                            }
                          }}
                          sx={{
                            marginBottom: 0.75,
                            backgroundColor: getStatusColor(unit.status),
                            border: selectedUnits.has(unit.id) ? '2px solid #3c8cff' : '1px solid #40444b',
                            borderRadius: 1,
                            padding: 0.75,
                            display: 'flex',
                            alignItems: 'center',
                            minHeight: 40,
                            position: 'relative',
                            cursor: unit.id !== 'DISPATCH' ? 'grab' : 'default',
                            opacity: draggedUnitIds?.includes(unit.id) ? 0.5 : 1,
                            transition: 'opacity 0.2s ease'
                          }}
                        >
                          {/* Click target for selection */}
                          <Box
                            sx={{
                              position: 'absolute',
                              left: 0,
                              top: 0,
                              right: unit.id === 'DISPATCH' ? 120 : 150,
                              bottom: 0,
                              cursor: unit.id === 'DISPATCH' ? 'default' : 'pointer',
                              zIndex: 1
                            }}
                            onClick={() => unit.id !== 'DISPATCH' && handleUnitSelect(unit.id, !selectedUnits.has(unit.id))}
                          >
                            {/* Selection checkbox */}
                            {unit.id !== 'DISPATCH' && (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  left: 6,
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  width: 20,
                                  height: 20,
                                  backgroundColor: '#1a1d22',
                                  border: '1px solid #464a52',
                                  borderRadius: 0.5,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  zIndex: 2
                                }}
                              >
                                {selectedUnits.has(unit.id) && (
                                  <Typography sx={{ 
                                    color: '#eceef1',
                                    fontSize: 16,
                                    fontWeight: 'bold',
                                    lineHeight: 1
                                  }}>
                                    ✓
                                  </Typography>
                                )}
                              </Box>
                            )}

                            {/* Unit ID chip with warning icon */}
                            <Box sx={{ 
                              position: 'absolute',
                              left: isDispatchUnit(unit) ? 8 : 34,
                              top: '50%',
                              transform: 'translateY(-50%)',
                              zIndex: 2,
                              display: 'flex',
                              alignItems: 'center'
                            }}>
                              {(() => {
                                const showWarn = isUnitUnclaimed(unit) && !isDispatchUnit(unit);
                                return (
                                  <Tooltip title={showWarn ? 'Unit has no claimers' : ''} disableHoverListener={!showWarn}>
                                    <Chip
                                      icon={showWarn ? <WarningIcon /> : undefined}
                                      label={
                                        <Typography sx={{ fontSize: 16, fontWeight: 'bold', lineHeight: 1 }}>
                                          {unit.id}
                                        </Typography>
                                      }
                                      size="small"
                                      sx={{
                                        backgroundColor: '#2a2d32',
                                        color: '#eceef1',
                                        height: 32,
                                        borderRadius: '4px',
                                        '& .MuiChip-label': { px: 1 },
                                        '& .MuiChip-icon': {
                                          color: '#ff6b35',
                                          fontSize: 18,
                                          mr: 0.5,
                                        },
                                      }}
                                    />
                                  </Tooltip>
                                );
                              })()}
                            </Box>
                          </Box>

                          {/* Status dropdown or chip */}
                          {unit.id === 'DISPATCH' ? (
                            <Box sx={{
                              position: 'absolute',
                              right: 8,
                              top: '50%',
                              transform: 'translateY(-50%)'
                            }}>
                              <Chip
                                label={unit.status || '—'}
                                size="small"
                           sx={{
                             backgroundColor: '#2a2d32',
                             color: '#eceef1',
                             fontSize: 16,
                             fontWeight: 'bold',
                             height: 32,
                             borderRadius: '4px',
                             '& .MuiChip-label': {
                               padding: '0 8px'
                             }
                           }}
                              />
                            </Box>
                          ) : (
                            <Box
                              sx={{
                                position: 'absolute',
                                right: 4,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                width: 100,
                                height: 32,
                                zIndex: 3
                              }}
                            >
                              <Select
                                value={unit.status || ''}
                                onChange={(e: any) => handleUnitStatusChange(unit.id, e.target.value as string)}
                                size="small"
                                sx={{
                                  width: '100%',
                                  height: '100%',
                                  backgroundColor: '#1a1d22',
                                  color: '#eceef1',
                                  '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#464a52',
                                  },
                                  '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#3c8cff',
                                  },
                                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#3c8cff',
                                  },
                                  '& .MuiSelect-select': {
                                    padding: '6px 12px',
                                    fontSize: 14,
                                    fontWeight: 'bold'
                                  },
                                  '& .MuiSelect-icon': {
                                    color: '#eceef1'
                                  }
                                }}
                              >
                                {['I/S', 'E/R', 'O/S', 'TRX', 'OOS', 'DISP'].map((status) => (
                                  <MenuItem 
                                    key={status} 
                                    value={status} 
                                    disabled={status === 'DISP' && unit.id !== 'DISPATCH'}
                                    sx={{ 
                                      color: '#eceef1',
                                      backgroundColor: '#1a1d22',
                                      '&:hover': { backgroundColor: '#2a2a2a' },
                                      '&.Mui-selected': { backgroundColor: '#3c8cff' },
                                      '&.Mui-disabled': { color: '#666' }
                                    }}
                                  >
                                    {status}
                                  </MenuItem>
                                ))}
                              </Select>
                            </Box>
                          )}
                        </Box>
                      ))
                    ) : (
                      // Nested grouping (by type and status)
                      Object.entries(groupValue).map(([subGroupKey, subGroupUnits]) => (
                        <Box key={subGroupKey} sx={{ marginLeft: 2, marginBottom: 1 }}>
                          <Typography sx={{ 
                            color: '#eceef1', 
                            fontSize: 12, 
                            fontWeight: 'bold', 
                            marginBottom: 0.5,
                            paddingLeft: 1
                          }}>
                            {subGroupKey} ({(subGroupUnits as Unit[]).length})
                          </Typography>
                          {(subGroupUnits as Unit[]).map((unit: Unit) => (
                            <Box
                              key={unit.id}
                              draggable={unit.id !== 'DISPATCH'}
                              onContextMenu={(e: React.MouseEvent) => handleContextMenu(e, unit.id)}
                              onDragStart={(e: React.DragEvent) => {
                                // If this unit is selected, drag all selected units; otherwise just this one
                                const unitsToDrag = selectedUnits.has(unit.id) && selectedUnits.size > 0 
                                  ? Array.from(selectedUnits) 
                                  : [unit.id];
                                e.dataTransfer.setData('text/plain', JSON.stringify(unitsToDrag));
                                e.dataTransfer.effectAllowed = 'copy';
                                setDraggedUnitIds(unitsToDrag);
                                
                                // Create custom drag image with stacked unit rows
                                if (unitsToDrag.length > 1) {
                                  const dragContainer = document.createElement('div');
                                  dragContainer.style.cssText = 'position: absolute; top: -10000px; left: -10000px; width: 280px;';
                                  
                                  (unitsToDrag.slice(0, 5) as string[]).forEach((unitId: string, index: number) => {
                                    const dragUnit = units.find((u: Unit) => u.id === unitId);
                                    if (dragUnit) {
                                      const unitRow = document.createElement('div');
                                      unitRow.style.cssText = `
                                        background: ${getStatusColor(dragUnit.status)};
                                        border: 2px solid #3c8cff;
                                        border-radius: 4px;
                                        padding: 6px 12px;
                                        margin-bottom: ${index < Math.min(unitsToDrag.length, 5) - 1 ? '4px' : '0'};
                                        display: flex;
                                        align-items: center;
                                        gap: 8px;
                                        min-height: 40px;
                                        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
                                      `;
                                      
                                      const unitChip = document.createElement('span');
                                      unitChip.style.cssText = 'background: #2a2d32; color: #eceef1; padding: 4px 12px; border-radius: 4px; font-weight: bold; font-size: 14px; font-family: system-ui, -apple-system, sans-serif;';
                                      unitChip.textContent = unitId;
                                      
                                      const statusChip = document.createElement('span');
                                      statusChip.style.cssText = 'background: #1a1d22; color: #eceef1; padding: 4px 12px; border-radius: 4px; font-weight: bold; font-size: 12px; margin-left: auto; font-family: system-ui, -apple-system, sans-serif;';
                                      statusChip.textContent = dragUnit.status || 'I/S';
                                      
                                      unitRow.appendChild(unitChip);
                                      unitRow.appendChild(statusChip);
                                      dragContainer.appendChild(unitRow);
                                    }
                                  });
                                  
                                  // Add "+X more" indicator if there are more than 5 units
                                  if (unitsToDrag.length > 5) {
                                    const moreIndicator = document.createElement('div');
                                    moreIndicator.style.cssText = 'background: #3c8cff; color: white; padding: 6px 12px; border-radius: 4px; text-align: center; font-weight: bold; font-size: 12px; margin-top: 4px; font-family: system-ui, -apple-system, sans-serif;';
                                    moreIndicator.textContent = `+${unitsToDrag.length - 5} more`;
                                    dragContainer.appendChild(moreIndicator);
                                  }
                                  
                                  document.body.appendChild(dragContainer);
                                  e.dataTransfer.setDragImage(dragContainer, 140, 20);
                                  setTimeout(() => document.body.removeChild(dragContainer), 0);
                                }
                              }}
                              sx={{
                                marginBottom: 0.75,
                                backgroundColor: getStatusColor(unit.status),
                                border: selectedUnits.has(unit.id) ? '2px solid #3c8cff' : '1px solid #40444b',
                                borderRadius: 1,
                                padding: 0.75,
                                display: 'flex',
                                alignItems: 'center',
                                minHeight: 40,
                                position: 'relative',
                                cursor: unit.id !== 'DISPATCH' ? 'grab' : 'default',
                                opacity: draggedUnitIds?.includes(unit.id) ? 0.5 : 1,
                                transition: 'opacity 0.2s ease'
                              }}
                            >
                              {/* Click target for selection */}
                              <Box
                                sx={{
                                  position: 'absolute',
                                  left: 0,
                                  top: 0,
                                  right: unit.id === 'DISPATCH' ? 120 : 150,
                                  bottom: 0,
                                  cursor: unit.id === 'DISPATCH' ? 'default' : 'pointer',
                                  zIndex: 1
                                }}
                                onClick={() => unit.id !== 'DISPATCH' && handleUnitSelect(unit.id, !selectedUnits.has(unit.id))}
                              >
                                {/* Selection checkbox */}
                                {unit.id !== 'DISPATCH' && (
                                  <Box
                                    sx={{
                                      position: 'absolute',
                                      left: 6,
                                      top: '50%',
                                      transform: 'translateY(-50%)',
                                      width: 20,
                                      height: 20,
                                      backgroundColor: '#1a1d22',
                                      border: '1px solid #464a52',
                                      borderRadius: 0.5,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      zIndex: 2
                                    }}
                                  >
                                    {selectedUnits.has(unit.id) && (
                                      <Typography sx={{ 
                                        color: '#eceef1',
                                        fontSize: 16,
                                        fontWeight: 'bold',
                                        lineHeight: 1
                                      }}>
                                        ✓
                                      </Typography>
                                    )}
                                  </Box>
                                )}

                                {/* Unit ID chip with warning icon */}
                                <Box sx={{ 
                                  position: 'absolute',
                                  left: isDispatchUnit(unit) ? 8 : 34,
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  zIndex: 2,
                                  display: 'flex',
                                  alignItems: 'center'
                                }}>
                                  {(() => {
                                    const showWarn = isUnitUnclaimed(unit) && !isDispatchUnit(unit);
                                    return (
                                      <Tooltip title={showWarn ? 'Unit has no claimers' : ''} disableHoverListener={!showWarn}>
                                        <Chip
                                          icon={showWarn ? <WarningIcon /> : undefined}
                                          label={
                                            <Typography sx={{ fontSize: 16, fontWeight: 'bold', lineHeight: 1 }}>
                                              {unit.id}
                                            </Typography>
                                          }
                                          size="small"
                                          sx={{
                                            backgroundColor: '#2a2d32',
                                            color: '#eceef1',
                                            height: 32,
                                            borderRadius: '4px',
                                            '& .MuiChip-label': { px: 1 },
                                            '& .MuiChip-icon': {
                                              color: '#ff6b35',
                                              fontSize: 18,
                                              mr: 0.5,
                                            },
                                          }}
                                        />
                                      </Tooltip>
                                    );
                                  })()}
                                </Box>
                              </Box>

                              {/* Status dropdown or chip */}
                              {unit.id === 'DISPATCH' ? (
                                <Box sx={{
                                  position: 'absolute',
                                  right: 8,
                                  top: '50%',
                                  transform: 'translateY(-50%)'
                                }}>
                                  <Chip
                                    label={unit.status || '—'}
                                    size="small"
                           sx={{
                             backgroundColor: '#2a2d32',
                             color: '#eceef1',
                             fontSize: 16,
                             fontWeight: 'bold',
                             height: 32,
                             borderRadius: '4px',
                             '& .MuiChip-label': {
                               padding: '0 8px'
                             }
                           }}
                                  />
                                </Box>
                              ) : (
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    right: 4,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    width: 100,
                                    height: 32,
                                    zIndex: 3
                                  }}
                                >
                                  <Select
                                    value={unit.status || ''}
                                    onChange={(e: any) => handleUnitStatusChange(unit.id, e.target.value as string)}
                                    size="small"
                                    sx={{
                                      width: '100%',
                                      height: '100%',
                                      backgroundColor: '#1a1d22',
                                      color: '#eceef1',
                                      '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#464a52',
                                      },
                                      '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#3c8cff',
                                      },
                                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#3c8cff',
                                      },
                                      '& .MuiSelect-select': {
                                        padding: '6px 12px',
                                        fontSize: 14,
                                        fontWeight: 'bold'
                                      },
                                      '& .MuiSelect-icon': {
                                        color: '#eceef1'
                                      }
                                    }}
                                  >
                                    {['I/S', 'E/R', 'O/S', 'TRX', 'OOS', 'DISP'].map((status) => (
                                      <MenuItem 
                                        key={status} 
                                        value={status} 
                                        disabled={status === 'DISP' && unit.id !== 'DISPATCH'}
                                        sx={{ 
                                          color: '#eceef1',
                                          backgroundColor: '#1a1d22',
                                          '&:hover': { backgroundColor: '#2a2a2a' },
                                          '&.Mui-selected': { backgroundColor: '#3c8cff' },
                                          '&.Mui-disabled': { color: '#666' }
                                        }}
                                      >
                                        {status}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </Box>
                              )}
                            </Box>
                          ))}
                        </Box>
                      ))
                    )}
                  </Box>
                ));
              }
              
              // Render flat list (no grouping)
              return (filteredAndGroupedUnits as Unit[]).map((unit) => {
              const isDispatchUnit = unit.id === 'DISPATCH';
              const isSelected = selectedUnits.has(unit.id);
              
              return (
                <Box
                  key={unit.id}
                  draggable={!isDispatchUnit}
                  onContextMenu={(e: React.MouseEvent) => handleContextMenu(e, unit.id)}
                  onDragStart={(e: React.DragEvent) => {
                    // If this unit is selected, drag all selected units; otherwise just this one
                    const unitsToDrag = isSelected && selectedUnits.size > 0 
                      ? Array.from(selectedUnits) 
                      : [unit.id];
                    e.dataTransfer.setData('text/plain', JSON.stringify(unitsToDrag));
                    e.dataTransfer.effectAllowed = 'copy';
                    setDraggedUnitIds(unitsToDrag);
                    
                    // Create custom drag image with stacked unit rows
                    if (unitsToDrag.length > 1) {
                      const dragContainer = document.createElement('div');
                      dragContainer.style.cssText = 'position: absolute; top: -10000px; left: -10000px; width: 280px;';
                      
                                  (unitsToDrag.slice(0, 5) as string[]).forEach((unitId: string, index: number) => {
                        const dragUnit = units.find((u: Unit) => u.id === unitId);
                        if (dragUnit) {
                          const unitRow = document.createElement('div');
                          unitRow.style.cssText = `
                            background: ${getStatusColor(dragUnit.status)};
                            border: 2px solid #3c8cff;
                            border-radius: 4px;
                            padding: 6px 12px;
                            margin-bottom: ${index < Math.min(unitsToDrag.length, 5) - 1 ? '4px' : '0'};
                            display: flex;
                            align-items: center;
                            gap: 8px;
                            min-height: 40px;
                            box-shadow: 0 2px 8px rgba(0,0,0,0.3);
                          `;
                          
                          const unitChip = document.createElement('span');
                          unitChip.style.cssText = 'background: #2a2d32; color: #eceef1; padding: 4px 12px; border-radius: 4px; font-weight: bold; font-size: 14px; font-family: system-ui, -apple-system, sans-serif;';
                          unitChip.textContent = unitId;
                          
                          const statusChip = document.createElement('span');
                          statusChip.style.cssText = 'background: #1a1d22; color: #eceef1; padding: 4px 12px; border-radius: 4px; font-weight: bold; font-size: 12px; margin-left: auto; font-family: system-ui, -apple-system, sans-serif;';
                          statusChip.textContent = dragUnit.status || 'I/S';
                          
                          unitRow.appendChild(unitChip);
                          unitRow.appendChild(statusChip);
                          dragContainer.appendChild(unitRow);
                        }
                      });
                      
                      // Add "+X more" indicator if there are more than 5 units
                      if (unitsToDrag.length > 5) {
                        const moreIndicator = document.createElement('div');
                        moreIndicator.style.cssText = 'background: #3c8cff; color: white; padding: 6px 12px; border-radius: 4px; text-align: center; font-weight: bold; font-size: 12px; margin-top: 4px; font-family: system-ui, -apple-system, sans-serif;';
                        moreIndicator.textContent = `+${unitsToDrag.length - 5} more`;
                        dragContainer.appendChild(moreIndicator);
                      }
                      
                      document.body.appendChild(dragContainer);
                      e.dataTransfer.setDragImage(dragContainer, 140, 20);
                      setTimeout(() => document.body.removeChild(dragContainer), 0);
                    }
                  }}
                  sx={{
                    marginBottom: 0.75,
                    backgroundColor: getStatusColor(unit.status),
                      border: isSelected ? '2px solid #3c8cff' : '1px solid #40444b',
                    borderRadius: 1,
                      padding: 0.75,
                    display: 'flex',
                    alignItems: 'center',
                      minHeight: 40,
                    position: 'relative',
                    cursor: !isDispatchUnit ? 'grab' : 'default',
                    opacity: draggedUnitIds?.includes(unit.id) ? 0.5 : 1,
                    transition: 'opacity 0.2s ease'
                  }}
                >
                    {/* Click target for selection */}
                  <Box
                    sx={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                        right: isDispatchUnit ? 120 : 150,
                      bottom: 0,
                      cursor: isDispatchUnit ? 'default' : 'pointer',
                      zIndex: 1
                    }}
                    onClick={() => !isDispatchUnit && handleUnitSelect(unit.id, !isSelected)}
                  >
                      {/* Selection checkbox */}
                    {!isDispatchUnit && (
                      <Box
                        sx={{
                          position: 'absolute',
                          left: 6,
                          top: '50%',
                          transform: 'translateY(-50%)',
                            width: 20,
                          height: 20,
                            backgroundColor: '#1a1d22',
                            border: '1px solid #464a52',
                          borderRadius: 0.5,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          zIndex: 2
                        }}
                      >
                        {isSelected && (
                          <Typography sx={{ 
                              color: '#eceef1',
                            fontSize: 16,
                            fontWeight: 'bold',
                            lineHeight: 1
                          }}>
                            ✓
                          </Typography>
                        )}
                      </Box>
                    )}

                      {/* Unit ID chip with warning icon */}
                      <Box sx={{ 
                      position: 'absolute',
                        left: isDispatchUnit ? 8 : 34,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      zIndex: 2,
                        display: 'flex',
                        alignItems: 'center'
                    }}>
                      {(() => {
                        const showWarn = isUnitUnclaimed(unit) && !isDispatchUnit;
                        return (
                          <Tooltip title={showWarn ? 'Unit has no claimers' : ''} disableHoverListener={!showWarn}>
                            <Chip
                              icon={showWarn ? <WarningIcon /> : undefined}
                              label={
                                <Typography sx={{ fontSize: 14, fontWeight: 'bold', lineHeight: 1 }}>
                                  {unit.id}
                                </Typography>
                              }
                              size="small"
                              sx={{
                                backgroundColor: '#2a2d32',
                                color: '#eceef1',
                                height: 32,
                                borderRadius: '4px',
                                '& .MuiChip-label': { px: 1 },
                                '& .MuiChip-icon': {
                                  color: '#ff6b35',
                                  fontSize: 18,
                                  mr: 0.5,
                                },
                              }}
                            />
                          </Tooltip>
                        );
                      })()}
                      </Box>
                  </Box>

                    {/* Status dropdown or chip */}
                  {!isDispatchUnit ? (
                    <Box
                      sx={{
                        position: 'absolute',
                        right: 4,
                        top: '50%',
                        transform: 'translateY(-50%)',
                          width: 100,
                          height: 32,
                        zIndex: 3
                      }}
                    >
                      <Select
                        value={unit.status || ''}
                        onChange={(e: any) => handleUnitStatusChange(unit.id, e.target.value as string)}
                        size="small"
                        sx={{
                          width: '100%',
                          height: '100%',
                            backgroundColor: '#1a1d22',
                            color: '#eceef1',
                          '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#464a52',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#3c8cff',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#3c8cff',
                          },
                          '& .MuiSelect-select': {
                            padding: '6px 12px',
                            fontSize: 14,
                            fontWeight: 'bold'
                          },
                          '& .MuiSelect-icon': {
                              color: '#eceef1'
                            }
                          }}
                        >
                          {['I/S', 'E/R', 'O/S', 'TRX', 'OOS', 'DISP'].map((status) => (
                            <MenuItem 
                              key={status} 
                              value={status} 
                              disabled={status === 'DISP' && unit.id !== 'DISPATCH'}
                              sx={{ 
                                color: '#eceef1',
                                backgroundColor: '#1a1d22',
                                '&:hover': { backgroundColor: '#2a2a2a' },
                                '&.Mui-selected': { backgroundColor: '#3c8cff' },
                                '&.Mui-disabled': { color: '#666' }
                              }}
                            >
                              {status}
                            </MenuItem>
                          ))}
                      </Select>
                    </Box>
                  ) : (
                      <Box sx={{
                      position: 'absolute',
                      right: 8,
                      top: '50%',
                        transform: 'translateY(-50%)'
                      }}>
                        <Chip
                          label={unit.status || '—'}
                          size="small"
                           sx={{
                             backgroundColor: '#2a2d32',
                             color: '#eceef1',
                             fontSize: 16,
                             fontWeight: 'bold',
                             height: 32,
                             borderRadius: '4px',
                             '& .MuiChip-label': {
                               padding: '0 8px'
                             }
                           }}
                        />
                      </Box>
                  )}
                </Box>
              );
              });
            })()}
          </Box>
        </Paper>

        {/* Right Panel - Calls and Details */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
          {/* Calls Section - Half the vertical height */}
          <Paper
            sx={{
              height: isCallDetailsMaximized ? 0 : '50%', // Slide up/down with height
              backgroundColor: '#1c1f25', // surface
              border: '1px solid #40444b', // border
              borderRadius: 1.5,
              padding: isCallDetailsMaximized ? 0 : 1.25, // Remove padding when hidden
              marginBottom: isCallDetailsMaximized ? 0 : 1.25, // Remove margin when hidden
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              minHeight: 0,
              transition: 'height 0.3s cubic-bezier(0.4, 0, 0.2, 1), padding 0.3s cubic-bezier(0.4, 0, 0.2, 1), margin 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              opacity: isCallDetailsMaximized ? 0 : 1,
              transitionProperty: 'height, padding, margin, opacity',
              transitionDuration: '0.3s',
              transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            {/* Calls Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 1 }}>
              <Typography variant="h6" sx={{ color: '#eceef1', fontSize: 16, fontWeight: 'bold', flexGrow: 1 }}> {/* text */}
                CALLS
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setShowCreateCallModal(true)}
                sx={{
                  backgroundColor: '#3c8cff', // accent
                  color: '#0a0c0e', // chipText
                  fontSize: 12,
                  minWidth: 80,
                  height: 26,
                  '&:hover': { backgroundColor: '#2a7ae6' }
                }}
              >
                NEW
              </Button>
            </Box>

            {/* Calls Table - Takes remaining space */}
            <Box sx={{ 
            flex: 1, 
            overflowY: 'scroll',
            overflowX: 'hidden',
            minHeight: 0,
            paddingRight: 1,
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#1a1d22',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#464a52',
              borderRadius: '4px',
              '&:hover': {
                background: '#5a5e66',
              },
            },
            // Always show vertical scrollbar, even when content doesn't overflow
            scrollbarWidth: 'thin',
            scrollbarColor: '#464a52 #1a1d22',
          }}>
              {Array.isArray(calls) && calls.filter(call => call.status !== 'CLOSED').length > 0 ? (
                <Box sx={{
                  backgroundColor: '#1c1f25',
                  border: '1px solid #40444b',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  width: '100%',
                  display: 'table',
                  tableLayout: 'fixed'
                }}>
                  {/* Table Header */}
                  <Box sx={{ 
                    display: 'flex',
                    backgroundColor: '#2a2d32',
                    borderBottom: '1px solid #40444b',
                    padding: '8px 0 8px 2px',
                    width: '100%'
                  }}>
                    <Box sx={{ 
                      width: `${columnWidths.id}%`, 
                      padding: '0 8px',
                      display: 'flex',
                      alignItems: 'center',
                      position: 'relative',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        right: 0,
                        top: '-8px',
                        bottom: '-1000px',
                        width: '2px',
                        backgroundColor: '#40444b',
                        zIndex: 1
                      }
                    }}>
                      <Typography sx={{ color: '#eceef1', fontSize: 12, fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5 }} onClick={() => handleSort('id')}>
                        ID <span style={{ width: '12px', textAlign: 'center' }}>{sortField === 'id' ? (sortDirection === 'asc' ? '↑' : '↓') : ''}</span>
                      </Typography>
                      <Box
                        sx={{
                          position: 'absolute',
                          right: 0,
                          top: '-8px',
                          bottom: '-1000px',
                          width: 8,
                          cursor: 'col-resize',
                          backgroundColor: isResizing === 'id' ? '#3c8cff' : 'transparent',
                          zIndex: 2,
                          '&:hover': { backgroundColor: '#3c8cff' }
                        }}
                        onMouseDown={(e: React.MouseEvent) => handleMouseDown('id', e)}
                      />
                    </Box>
                    <Box sx={{ 
                      width: `${columnWidths.description}%`, 
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        right: 0,
                        top: '-8px',
                        bottom: '-1000px',
                        width: '2px',
                        backgroundColor: '#40444b',
                        zIndex: 1
                      }, 
                      padding: '0 8px',
                      display: 'flex',
                      alignItems: 'center',
                      position: 'relative'
                    }}>
                      <Typography sx={{ color: '#eceef1', fontSize: 12, fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5 }} onClick={() => handleSort('callCode')}>
                        DESCRIPTION <span style={{ width: '12px', textAlign: 'center' }}>{sortField === 'callCode' ? (sortDirection === 'asc' ? '↑' : '↓') : ''}</span>
                      </Typography>
                      <Box
                        sx={{
                          position: 'absolute',
                          right: 0,
                          top: '-8px',
                          bottom: '-1000px',
                          width: 8,
                          cursor: 'col-resize',
                          backgroundColor: isResizing === 'description' ? '#3c8cff' : 'transparent',
                          '&:hover': { backgroundColor: '#3c8cff' }
                        }}
                        onMouseDown={(e: React.MouseEvent) => handleMouseDown('description', e)}
                      />
                    </Box>
                    <Box sx={{ 
                      width: `${columnWidths.location}%`, 
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        right: 0,
                        top: '-8px',
                        bottom: '-1000px',
                        width: '2px',
                        backgroundColor: '#40444b',
                        zIndex: 1
                      }, 
                      padding: '0 8px',
                      display: 'flex',
                      alignItems: 'center',
                      position: 'relative'
                    }}>
                      <Typography sx={{ color: '#eceef1', fontSize: 12, fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5 }} onClick={() => handleSort('location')}>
                        LOCATION <span style={{ width: '12px', textAlign: 'center' }}>{sortField === 'location' ? (sortDirection === 'asc' ? '↑' : '↓') : ''}</span>
                      </Typography>
                      <Box
                        sx={{
                          position: 'absolute',
                          right: 0,
                          top: '-8px',
                          bottom: '-1000px',
                          width: 8,
                          cursor: 'col-resize',
                          backgroundColor: isResizing === 'location' ? '#3c8cff' : 'transparent',
                          '&:hover': { backgroundColor: '#3c8cff' }
                        }}
                        onMouseDown={(e: React.MouseEvent) => handleMouseDown('location', e)}
                      />
                    </Box>
                    <Box sx={{ 
                      width: `${columnWidths.units}%`, 
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        right: 0,
                        top: '-8px',
                        bottom: '-1000px',
                        width: '2px',
                        backgroundColor: '#40444b',
                        zIndex: 1
                      }, 
                      padding: '0 8px',
                      display: 'flex',
                      alignItems: 'center',
                      position: 'relative'
                    }}>
                      <Typography sx={{ color: '#eceef1', fontSize: 12, fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5 }} onClick={() => handleSort('units')}>
                        UNITS <span style={{ width: '12px', textAlign: 'center' }}>{sortField === 'units' ? (sortDirection === 'asc' ? '↑' : '↓') : ''}</span>
                      </Typography>
                      <Box
                        sx={{
                          position: 'absolute',
                          right: 0,
                          top: '-8px',
                          bottom: '-1000px',
                          width: 8,
                          cursor: 'col-resize',
                          backgroundColor: isResizing === 'units' ? '#3c8cff' : 'transparent',
                          '&:hover': { backgroundColor: '#3c8cff' }
                        }}
                        onMouseDown={(e: React.MouseEvent) => handleMouseDown('units', e)}
                      />
                    </Box>
                    <Box sx={{ 
                      width: `${columnWidths.box}%`, 
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        right: 0,
                        top: '-8px',
                        bottom: '-1000px',
                        width: '2px',
                        backgroundColor: '#40444b',
                        zIndex: 1
                      }, 
                      padding: '0 8px',
                      display: 'flex',
                      alignItems: 'center',
                      position: 'relative'
                    }}>
                      <Typography sx={{ color: '#eceef1', fontSize: 12, fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5 }} onClick={() => handleSort('boxArea')}>
                        BOX <span style={{ width: '12px', textAlign: 'center' }}>{sortField === 'boxArea' ? (sortDirection === 'asc' ? '↑' : '↓') : ''}</span>
                      </Typography>
                      <Box
                        sx={{
                          position: 'absolute',
                          right: 0,
                          top: '-8px',
                          bottom: '-1000px',
                          width: 8,
                          cursor: 'col-resize',
                          backgroundColor: isResizing === 'box' ? '#3c8cff' : 'transparent',
                          '&:hover': { backgroundColor: '#3c8cff' }
                        }}
                        onMouseDown={(e: React.MouseEvent) => handleMouseDown('box', e)}
                      />
                    </Box>
                    <Box sx={{ 
                      width: `${columnWidths.time}%`, 
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        right: 0,
                        top: '-8px',
                        bottom: '-1000px',
                        width: '2px',
                        backgroundColor: '#40444b',
                        zIndex: 1
                      }, 
                      padding: '0 8px',
                      display: 'flex',
                      alignItems: 'center',
                      position: 'relative'
                    }}>
                      <Typography sx={{ color: '#eceef1', fontSize: 12, fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5 }} onClick={() => handleSort('timeRcvdUnix')}>
                        TIME <span style={{ width: '12px', textAlign: 'center' }}>{sortField === 'timeRcvdUnix' ? (sortDirection === 'asc' ? '↑' : '↓') : ''}</span>
                      </Typography>
                      <Box
                        sx={{
                          position: 'absolute',
                          right: 0,
                          top: '-8px',
                          bottom: '-1000px',
                          width: 8,
                          cursor: 'col-resize',
                          backgroundColor: isResizing === 'time' ? '#3c8cff' : 'transparent',
                          '&:hover': { backgroundColor: '#3c8cff' }
                        }}
                        onMouseDown={(e: React.MouseEvent) => handleMouseDown('time', e)}
                      />
                    </Box>
                    <Box sx={{ 
                      width: `${columnWidths.status}%`,
                      padding: '0 8px',
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      <Typography sx={{ color: '#eceef1', fontSize: 12, fontWeight: 'bold' }}>STATUS</Typography>
                    </Box>
                  </Box>
                  
                  {/* Table Rows */}
                  {getSortedCalls(calls.filter(call => call.status !== 'CLOSED')).map((call) => {
                    const isSelected = selectedCall?.id === call.id;
                    
                    // Use reopenedAt if call was reopened, otherwise use timeRcvdUnix (created_at)
                    const baseTime = call.reopenedAt 
                      ? Math.floor(new Date(call.reopenedAt).getTime() / 1000)
                      : call.timeRcvdUnix || 0;
                    
                    const elapsedTime = baseTime ? 
                      Math.floor((currentTime.getTime() / 1000) - baseTime) : 0;
                    const minutes = Math.floor(elapsedTime / 60);
                    const seconds = elapsedTime % 60;
                    const timeString = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
                    
                    return (
                      <Box
                        key={call.id}
                        onDragOver={(e: React.DragEvent) => {
                          e.preventDefault();
                          e.stopPropagation();
                          e.dataTransfer.dropEffect = 'copy';
                        }}
                        onDragEnter={(e: React.DragEvent) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (draggedUnitIds && draggedUnitIds.length > 0) {
                            // Check if any dragged units can be assigned to this call
                            const validUnits = draggedUnitIds.filter((unitId: string) => {
                              if (unitId === 'DISPATCH') return false;
                              const unit = units.find((u: Unit) => u.id === unitId);
                              return !(unit && unit.assignedCall === call.id);
                            });
                            
                            // Show drop zone if at least one unit can be assigned
                            if (validUnits.length > 0) {
                              setDragOverCallId(call.id);
                            }
                          }
                        }}
                        onDragLeave={(e: React.DragEvent) => {
                          e.preventDefault();
                          e.stopPropagation();
                          // Only clear if we're leaving the entire row, not just a child element
                          if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                            setDragOverCallId(null);
                          }
                        }}
                        onDrop={async (e: React.DragEvent) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setDragOverCallId(null);
                          setDraggedUnitIds(null);
                          
                          const dataString = e.dataTransfer.getData('text/plain');
                          if (!dataString) return;
                          
                          console.log('[Drag & Drop] Received drag data:', dataString);
                          
                          // Parse unit IDs (could be single unit or array)
                          let unitIds: string[] = [];
                          try {
                            const parsed = JSON.parse(dataString);
                            unitIds = Array.isArray(parsed) ? parsed : [parsed];
                            console.log('[Drag & Drop] Parsed unit IDs:', unitIds);
                          } catch (error) {
                            // Fallback for old format (single string) - only if it doesn't look like JSON
                            console.warn('Failed to parse drag data as JSON, treating as single unit ID:', dataString);
                            if (!dataString.startsWith('[') && !dataString.startsWith('{')) {
                              unitIds = [dataString];
                            } else {
                              console.error('Invalid drag data format:', dataString, error);
                              showError('Invalid drag data format');
                              return;
                            }
                          }
                          
                          // Filter out DISPATCH and ensure we have valid unit IDs
                          unitIds = unitIds.filter(id => id && typeof id === 'string' && id !== 'DISPATCH');
                          console.log('[Drag & Drop] Filtered unit IDs:', unitIds);
                          if (unitIds.length === 0) return;
                          
                          // Check if call is closed
                          if (call.status === 'CLOSED') {
                            showWarning(`Cannot assign units to closed call ${call.id}`);
                            return;
                          }
                          
                          // Validate and filter units
                          const alreadyAssigned: string[] = [];
                          const invalidStatus: string[] = [];
                          const validUnits: string[] = [];
                          
                          unitIds.forEach((unitId: string) => {
                            const unit = units.find((u: Unit) => u.id === unitId);
                            
                            if (unit && unit.assignedCall === call.id) {
                              alreadyAssigned.push(unitId);
                            } else if (unit && unit.status !== 'I/S') {
                              invalidStatus.push(unitId);
                            } else {
                              validUnits.push(unitId);
                            }
                          });
                          
                          // Show warnings for invalid units
                          if (alreadyAssigned.length > 0) {
                            showWarning(`Units already assigned to ${call.id}: ${alreadyAssigned.join(', ')}`);
                          }
                          
                          if (invalidStatus.length > 0) {
                            showWarning(`Units must be I/S to assign: ${invalidStatus.join(', ')}`);
                          }
                          
                          // If no valid units, return
                          if (validUnits.length === 0) {
                            return;
                          }
                          
                          // Assign valid units
                          try {
                            console.log('[Drag & Drop] Assigning units:', {
                              callId: call.id,
                              unitIds: validUnits,
                              unitIdsType: typeof validUnits,
                              isArray: Array.isArray(validUnits),
                              count: validUnits.length
                            });
                            
                            const response = await http.post('/calls/assign', {
                              callId: call.id,
                              unitIds: validUnits
                            });
                            
                            if (response.status === 200) {
                              setSelectedUnits(new Set());
                              showSuccess(`Assigned ${validUnits.length} unit(s) to call ${call.id}`);
                              // State will be updated via authoritative 'state-update' event from server
                            } else {
                              showError('Failed to assign units to call');
                            }
                          } catch (error) {
                            console.error('Failed to assign units to call:', error);
                            showError('Failed to assign units to call');
                          }
                        }}
                        sx={{
                          display: 'flex',
                          padding: '8px 0 8px 2px',
                          cursor: 'pointer',
                          backgroundColor: dragOverCallId === call.id ? '#3c8cff' : (isSelected ? '#2a2d32' : 'transparent'),
                          borderLeft: isSelected ? '3px solid #3c8cff' : '3px solid transparent',
                          borderBottom: '1px solid #40444b',
                          opacity: dragOverCallId === call.id ? 0.8 : 1,
                          '&:hover': { backgroundColor: dragOverCallId === call.id ? '#3c8cff' : '#2a2d32' }
                        }}
                        onClick={() => setSelectedCall(call)}
                      >
                        <Box sx={{ 
                          width: `${columnWidths.id}%`, 
                          padding: '0 8px',
                          display: 'flex',
                          alignItems: 'center',
                          minWidth: 0,
                          overflow: 'hidden'
                        }}>
                          <Typography sx={{ color: '#eceef1', fontSize: 13, fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>
                            {call.id}
                          </Typography>
                        </Box>
                        <Box sx={{ 
                          width: `${columnWidths.description}%`, 
                          padding: '0 8px',
                          display: 'flex',
                          alignItems: 'center',
                          minWidth: 0,
                          overflow: 'hidden'
                        }}>
                          <Typography sx={{ color: '#eceef1', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>
                            {getCallDescription(call.callCode)}
                          </Typography>
                        </Box>
                        <Box sx={{ 
                          width: `${columnWidths.location}%`, 
                          padding: '0 8px',
                          display: 'flex',
                          alignItems: 'center',
                          minWidth: 0,
                          overflow: 'hidden'
                        }}>
                          <Typography sx={{ color: '#eceef1', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>
                            {call.location.toUpperCase()}
                          </Typography>
                        </Box>
                        <Box sx={{ 
                          width: `${columnWidths.units}%`, 
                          padding: '0 8px',
                          display: 'flex',
                          alignItems: 'center',
                          minWidth: 0,
                          overflow: 'hidden'
                        }}>
                          <Typography sx={{ color: '#eceef1', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>
                            {call.units ? Object.keys(call.units).join(', ') : '—'}
                          </Typography>
                        </Box>
                        <Box sx={{ 
                          width: `${columnWidths.box}%`, 
                          padding: '0 8px',
                          display: 'flex',
                          alignItems: 'center',
                          minWidth: 0,
                          overflow: 'hidden'
                        }}>
                          <Typography sx={{ color: '#eceef1', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>
                            {call.boxArea || '—'}
                          </Typography>
                        </Box>
                        <Box sx={{ 
                          width: `${columnWidths.time}%`, 
                          padding: '0 8px',
                          display: 'flex',
                          alignItems: 'center',
                          minWidth: 0,
                          overflow: 'hidden'
                        }}>
                          <Typography sx={{ color: '#eceef1', fontSize: 13, fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>
                            {timeString}
                          </Typography>
                        </Box>
                        <Box sx={{ 
                          width: `${columnWidths.status}%`,
                          padding: '0 8px',
                          display: 'flex',
                          alignItems: 'center',
                          minWidth: 0,
                          overflow: 'hidden'
                        }}>
                          <Chip
                            label={getCallStatusLabel(getCallStatus(call))}
                            size="small"
                            onClick={getCallStatus(call) === 'PENDING_CONFIRMATION' ? () => handleCallConfirmation(call) : undefined}
                            sx={{
                              backgroundColor: getCallStatusColor(getCallStatus(call)),
                              color: '#ffffff',
                              fontSize: 10,
                              fontWeight: 'bold',
                              height: 20,
                              width: 'fit-content',
                              cursor: getCallStatus(call) === 'PENDING_CONFIRMATION' ? 'pointer' : 'default',
                              animation: getCallStatus(call) === 'PENDING_CONFIRMATION' ? 'pulse 1.5s infinite' : 'none',
                              '@keyframes pulse': {
                                '0%': { opacity: 1 },
                                '50%': { opacity: 0.6 },
                                '100%': { opacity: 1 }
                              },
                              '&:hover': getCallStatus(call) === 'PENDING_CONFIRMATION' ? {
                                backgroundColor: '#b91c1c'
                              } : {}
                            }}
                          />
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                <Typography sx={{ color: '#8c9198', textAlign: 'center', marginTop: 4 }}>
                  No active calls
                </Typography>
              )}
            </Box>
          </Paper>

          {/* Call Details Section - Takes remaining space */}
          <Paper
            sx={{
              flex: 1, // Always take remaining space
              backgroundColor: '#1c1f25', // surface
              border: '1px solid #40444b', // border
              borderRadius: 1.5,
              padding: 1.25,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              minHeight: 0
            }}
          >
            {selectedCall ? (
              <CallDetails 
                call={selectedCall} 
                units={units}
                selectedUnits={Array.from(selectedUnits)}
                onAttachUnits={handleAttachUnits}
                onCommentsViewed={() => {
                  // Comments are now managed internally by CallDetails
                }}
                currentUsername={user?.username}
                isMaximized={isCallDetailsMaximized}
                onToggleMaximize={() => setIsCallDetailsMaximized(!isCallDetailsMaximized)}
                onCallClosed={() => {
                  setIsCallDetailsMaximized(false);
                  setSelectedCall(null);
                }}
                showEditAndCloseButtons={true}
              />
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <Typography sx={{ color: '#666', fontSize: 16 }}>
                  Select a call to view details
                </Typography>
              </Box>
            )}
          </Paper>
        </Box>
      </Box>

      {/* Create Call Modal */}
      {showCreateCallModal && (
        <CreateCallModal
          open={showCreateCallModal}
          onClose={() => setShowCreateCallModal(false)}
          onCallCreated={handleCreateCall}
        />
      )}

      {/* Closed Calls View */}
      {showClosedCalls && (
        <ClosedCallsView
          onClose={() => setShowClosedCalls(false)}
          userRole={user?.role || 'unit'}
        />
      )}

      {/* Shift Commander Interface */}
      {hasPermission('shift-commander') && showShiftCommander && (
        <ShiftCommanderInterface
          onClose={() => setShowShiftCommander(false)}
        />
      )}

      {/* Admin Interface Dialog */}
      {hasPermission('admin') && (
        <AdminInterface 
          open={showAdminDialog} 
          onClose={() => setShowAdminDialog(false)} 
        />
      )}

      {/* Call Status Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, call: null })} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#1a1d22', color: '#eceef1' }}>
          Confirm Call Status
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: '#1a1d22', color: '#eceef1' }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            All units have cleared from call <strong>{confirmDialog.call?.incidentNumber}</strong>.
            What would you like to do with this call?
          </Typography>
          <TextField
            fullWidth
            label="Reason (optional)"
            value={confirmationReason}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirmationReason(e.target.value)}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                color: '#eceef1',
                '& fieldset': { borderColor: '#464a52' },
                '&:hover fieldset': { borderColor: '#3c8cff' },
                '&.Mui-focused fieldset': { borderColor: '#3c8cff' }
              },
              '& .MuiInputLabel-root': { color: '#b4b9c0' },
              '& .MuiInputLabel-root.Mui-focused': { color: '#3c8cff' }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ backgroundColor: '#1a1d22', padding: 3 }}>
          <Button
            onClick={() => handleConfirmCallStatus('pending')}
            variant="outlined"
            sx={{
              borderColor: '#8b4513',
              color: '#8b4513',
              '&:hover': {
                borderColor: '#a0522d',
                backgroundColor: 'rgba(139, 69, 19, 0.1)'
              }
            }}
          >
            Set to Pending
          </Button>
          <Button
            onClick={() => handleConfirmCallStatus('closed')}
            variant="contained"
            sx={{
              backgroundColor: '#28502a',
              color: '#c8ffd2',
              '&:hover': {
                backgroundColor: '#1e3a1e'
              }
            }}
          >
            Close Call
          </Button>
        </DialogActions>
      </Dialog>

      <AccountDialog
        open={showAccountDialog}
        onClose={() => setShowAccountDialog(false)}
        user={user}
        onLogout={handleLogout}
      />

      <FilterDialog
        open={showFilterDialog}
        onClose={() => setShowFilterDialog(false)}
        units={units}
        selectedUnitTypes={selectedUnitTypes}
        setSelectedUnitTypes={setSelectedUnitTypes}
        selectedStatuses={selectedStatuses}
        setSelectedStatuses={setSelectedStatuses}
        groupByType={groupByType}
        setGroupByType={setGroupByType}
        groupByStatus={groupByStatus}
        setGroupByStatus={setGroupByStatus}
        hideOOS={hideOOS}
      />

      {/* Manage Unit Modal */}
      <Dialog 
        open={showManageUnitModal} 
        onClose={handleCloseManageUnitModal} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle sx={{ backgroundColor: '#1a1d22', color: '#eceef1' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SettingsIcon />
            Manage Unit - {selectedUnitForManagement?.id}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: '#1a1d22', color: '#eceef1' }}>
          {selectedUnitForManagement && (
            <Box sx={{ mt: 2 }}>
              {/* Unit Information */}
              <Paper sx={{ p: 2, mb: 3, backgroundColor: '#2a2d32' }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Unit Information</Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Unit ID:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {selectedUnitForManagement.id}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Type:</Typography>
                    <Typography variant="body1">{selectedUnitForManagement.type || 'Unknown'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Status:</Typography>
                    <Chip 
                      label={selectedUnitForManagement.status || 'Unknown'} 
                      size="small"
                      sx={{ 
                        backgroundColor: getStatusColor(selectedUnitForManagement.status || ''), 
                        color: '#000',
                        fontWeight: 'bold'
                      }}
                    />
                  </Box>
                </Box>
              </Paper>

              {/* Current Assignments */}
              <Typography variant="h6" sx={{ mb: 2 }}>
                {hasActiveShift ? 'Current Assignments' : 'Current Claimers'}
              </Typography>
              {loadingUnitAssignments ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <List sx={{ backgroundColor: '#2a2d32', borderRadius: 1 }}>
                  {unitAssignments.length > 0 ? (
                    unitAssignments.map((assignment) => (
                      <ListItem key={assignment.id} divider>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                {assignment.personnelName}
                              </Typography>
                              <Chip 
                                label={assignment.role} 
                                size="small"
                                variant="outlined"
                                sx={{ color: '#eceef1' }}
                              />
                            </Box>
                          }
                          secondary={
                            <Typography variant="body2" color="text.secondary">
                              Callsign: {assignment.callsign}
                            </Typography>
                          }
                        />
                        <ListItemSecondaryAction>
                          {(assignment as any).isClaim ? (
                            // For claims, show "Unclaim" button
                            <IconButton
                              edge="end"
                              onClick={() => handleUnclaimUnit()}
                              sx={{ color: '#ff6b35' }}
                              title="Unclaim unit"
                            >
                              <PersonRemoveIcon />
                            </IconButton>
                          ) : (
                            // For assignments, show "Remove" button
                            <IconButton
                              edge="end"
                              onClick={() => handleRemoveUserFromUnit(assignment.id || '')}
                              sx={{ color: '#ff6b35' }}
                              title="Remove assignment"
                            >
                              <PersonRemoveIcon />
                            </IconButton>
                          )}
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))
                  ) : (
                    <ListItem>
                      <ListItemText
                        primary="No assignments found"
                        secondary={
                          hasActiveShift 
                            ? "Add users to assign them to this unit" 
                            : "No users have claimed this unit"
                        }
                      />
                    </ListItem>
                  )}
                </List>
              )}

              {/* Add User Assignment */}
              <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
                {hasActiveShift ? 'Add User Assignment' : 'Assign Unit to User'}
              </Typography>
              
              {!hasActiveShift && (
                <Box sx={{ 
                  backgroundColor: '#8b4513', 
                  color: '#fff', 
                  padding: 2, 
                  borderRadius: 1, 
                  mb: 2,
                  border: '1px solid #a0522d'
                }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    No Active Shift
                  </Typography>
                  <Typography variant="body2">
                    There is currently no active shift. You can manage unit claims below - 
                    assign units to users or remove existing claims.
                  </Typography>
                </Box>
              )}
              
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <Autocomplete
                  disabled={!hasActiveShift}
                  options={availableUsers.filter(user => {
                    // Filter out users who already have this specific role in this unit
                    if (selectedRoleForAssignment) {
                      return !unitAssignments.some(assignment => 
                        (assignment.userId ? assignment.userId === user.id : assignment.personnelName === user.name) && 
                        assignment.role === selectedRoleForAssignment
                      );
                    }
                    // If no role selected, show all users (backend will validate)
                    return true;
                  })}
                  getOptionLabel={(option: User) => option.username}
                  value={selectedUserForAssignment}
                  onChange={(_, newValue) => setSelectedUserForAssignment(newValue)}
                  sx={{ 
                    minWidth: 250,
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#2a2d32',
                      color: '#eceef1'
                    },
                    '& .MuiAutocomplete-input': {
                      color: '#eceef1'
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select User"
                      variant="outlined"
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          color: '#eceef1',
                          '& fieldset': {
                            borderColor: '#40444b'
                          },
                          '&:hover fieldset': {
                            borderColor: '#eceef1'
                          }
                        },
                        '& .MuiInputLabel-root': {
                          color: '#eceef1'
                        }
                      }}
                    />
                  )}
                />
                
                {hasActiveShift && (
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel sx={{ color: '#eceef1' }}>Role</InputLabel>
                    <Select
                      value={selectedRoleForAssignment}
                      onChange={(e) => setSelectedRoleForAssignment(e.target.value)}
                      label="Role"
                      sx={{
                        backgroundColor: '#2a2d32',
                        color: '#eceef1',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#40444b'
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#eceef1'
                        }
                      }}
                    >
                      {getAvailableRolesForUnit().map((role) => (
                        <MenuItem key={role} value={role} sx={{ color: '#eceef1' }}>
                          {role}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                <Button
                  variant="contained"
                  onClick={handleFormUserAssignment}
                  disabled={!selectedUserForAssignment || (hasActiveShift && !selectedRoleForAssignment)}
                  startIcon={<PersonAddIcon />}
                  sx={{
                    backgroundColor: '#3c8cff',
                    color: '#000',
                    '&:hover': {
                      backgroundColor: '#2c7ce8'
                    },
                    '&:disabled': {
                      backgroundColor: '#40444b',
                      color: '#6c6c6c'
                    }
                  }}
                >
                  Add
                </Button>
              </Box>
              {availableUsers.filter(user => {
                if (selectedRoleForAssignment) {
                  return !unitAssignments.some(assignment => 
                    (assignment.userId ? assignment.userId === user.id : assignment.personnelName === user.name) && 
                    assignment.role === selectedRoleForAssignment
                  );
                }
                return true;
              }).length === 0 && selectedRoleForAssignment && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  All available users already have the {selectedRoleForAssignment} role assigned to this unit
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ backgroundColor: '#1a1d22', color: '#eceef1' }}>
          <Button onClick={handleCloseManageUnitModal}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Context Menu */}
      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
        slotProps={{
          paper: {
            sx: {
              backgroundColor: '#2a2d32',
              color: '#eceef1',
              border: '1px solid #40444b',
              '& .MuiMenuItem-root': {
                color: '#eceef1',
                '&:hover': {
                  backgroundColor: '#40444b'
                }
              }
            }
          }
        }}
      >
        <MenuItem 
          onClick={() => contextMenu && handleManageUnit(contextMenu.unitId)}
          disabled={contextMenu?.unitId === 'DISPATCH'}
        >
          <SettingsIcon sx={{ mr: 1, fontSize: 20 }} />
          Manage Unit
        </MenuItem>
      </Menu>

      {/* Map Modal */}
      <MapModal
        open={showMapModal}
        onClose={() => {
          console.log('🗺️ MapModal onClose called');
          setShowMapModal(false);
        }}
      />
    </Box>
  );
};

export default DispatcherInterface;